import React, { useState, useEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { Lead, Column, FilterState, LeadsPayload, PaginationState, CustomFieldFilter } from '@/types/leads/Table';
import { DispositionTree, BucketType, DispositionOption } from '@/types/disposition';
import { optimizedApi } from '@/utils/api/optimized-api';
import { showToast } from '@/components/ui/Toast';
import { apiRequest } from '@/utils/api-request';
import ColumnsService from '@/utils/columnsCache';
import Button from '@/components/ui/Button';
import FSelect from '@/components/ui/FSelect';
import { getCurrentUserRole, ROLES } from '@/utils/permissions';
import PipelineSearch from '@/components/PipelineTable/PipelineSearch';
import PipelineFiltersEnhanced from '@/components/PipelineTable/PipelineFiltersEnhanced';
import AddLeadModal from '@/components/PipelineTable/AddLeadModal';
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Filter,
    Plus,
    Download,
    X,
    UserCheck,
    Bell,
    Users,
    ChevronDown,
    Check,
    Calendar,
    Phone,
    ShoppingCart,
    Copy
} from 'lucide-react';
import ColumnVisibilityDropdown from './ColumnVisibilityDropdown';
import { getBucketMode, getRowId } from './bucketMode';
import { format, parseISO } from 'date-fns';
import { cn } from '@/utils/cn';
import { displayName } from '@/utils/displayName';
import ExportModal from '@/components/ExportModal';
import InitiateCallModal from '@/components/OutboundCalling/InitiateCallModal';
import { getCachedOutboundConfig, cacheOutboundConfig, OutboundCallConfig } from '@/utils/outbound-calling';
import { useCallingState } from '@/contexts/CallingContext';
import { useUsers } from '@/contexts/UsersContext';
import { useUser } from '@/contexts/UserContext';
import { formatPhoneNumber } from '@/utils/phoneFormatter';

/**
 * One failed delivery attempt, as returned by /shopify/failed-delivery-leads.
 * The API sends the last three attempts oldest-first, so index 0 = NDR 1.
 */
interface NdrAttempt {
    attempt_date: string | null;
    ndr_code: number | null;
    reason: string | null;
    courier_remark: string | null;
    location: string | null;
    event_at: string | null;
}

const NDR_COLUMNS = ['ndr_1', 'ndr_2', 'ndr_3'] as const;

const getNdrAttempt = (lead: Lead, columnName: string): NdrAttempt | null => {
    const index = NDR_COLUMNS.indexOf(columnName as typeof NDR_COLUMNS[number]);
    if (index < 0) return null;
    const attempts = (lead as any).ndr_attempts as NdrAttempt[] | undefined;
    return attempts?.[index] ?? null;
};

const formatNdrDate = (value: string | null): string => {
    if (!value) return '';
    try {
        return format(parseISO(value), 'dd MMM');
    } catch {
        return value;
    }
};

interface EnhancedLeadsTableProps {
    onRowSelect?: (leadIds: string[]) => void;
    onLeadClick?: (lead: Lead) => void;
    agentCreatedOnly?: boolean; // When true, only show agent-created leads (hides toggle)
    excludeAgentCreated?: boolean; // When true, exclude agent-created leads (hides toggle)
    leadBucketFilter?: string; // e.g. 'phone' -- leads created purely from a call, no cart/order ever
    pageTitle?: string; // Custom page title
    pageIcon?: React.ReactNode; // Custom icon for the header
    apiEndpoint?: string; // Custom API endpoint (default: '/leads')
    hiddenColumns?: string[]; // Columns to hide from display
    showColumnSettings?: boolean; // Show/hide column visibility settings (default: true)
    ownerFilterUsers?: { uuid: string; userName: string }[]; // Custom users for owner filter dropdown
    maxDaysFuture?: number; // For reorder-leads: include leads due in next N days (default: 0)
}

export interface EnhancedLeadsTableRef {
    refreshLeads: () => Promise<void>;
}

const EnhancedLeadsTable = forwardRef<EnhancedLeadsTableRef, EnhancedLeadsTableProps>(({
    onRowSelect,
    onLeadClick,
    agentCreatedOnly = false,
    excludeAgentCreated = false,
    leadBucketFilter,
    pageTitle,
    pageIcon,
    apiEndpoint = '/leads',
    hiddenColumns = [],
    showColumnSettings = true,
    ownerFilterUsers,
    maxDaysFuture = 0
}, ref) => {
    // Which screen this table is rendering. Single source of truth — see
    // bucketMode.ts for why this replaced ten separate apiEndpoint.includes()
    // checks scattered through this file.
    const mode = useMemo(() => getBucketMode(apiEndpoint), [apiEndpoint]);

    // State management
    const [leads, setLeads] = useState<Lead[]>([]);
    const [columns, setColumns] = useState<Column[]>([]);
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set());
    const [pagination, setPagination] = useState<PaginationState>({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 25 // Changed from 15 to 25
    });
    const [activeFilters, setActiveFilters] = useState<FilterState>({});
    const [tempFilters, setTempFilters] = useState<FilterState>({}); // For inputs before applying
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [sortColumn, setSortColumn] = useState<string>('latest_incoming_date');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
    const [isBulkActionsOpen, setIsBulkActionsOpen] = useState(false);
    const [isChangeOwnerModalOpen, setIsChangeOwnerModalOpen] = useState(false);
    // Leads bulk-assign held back because another agent worked them in the last 48h.
    // The manager decides: keep them with that agent, or assign anyway.
    // Also held back: abandoned-cart customers who have already ordered.
    const [heldBack, setHeldBack] = useState<{
        leads: Array<{ lead_uuid: string; name: string | null; phone: string | null; worked_by: string | null; worked_at: string; outcome?: string | null }>;
        ordered: Array<{ lead_uuid: string; name: string | null; phone: string | null; order_number: string | null; ordered_at: string | null }>;
        ownerUuid: string;
        remark: string;
    } | null>(null);
    const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
    const [isBulkReminderModalOpen, setIsBulkReminderModalOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [selectedNewOwner, setSelectedNewOwner] = useState<string>('');
    const [ownerChangeRemark, setOwnerChangeRemark] = useState<string>('');
    const [isProcessingBulkAction, setIsProcessingBulkAction] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
    const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);
    const [dateRange, setDateRange] = useState<{ from: string | null; to: string | null; isDefault: boolean }>({ from: null, to: null, isDefault: false });

    // Courier / hub / status dropdown options for the EDD and NDR screens. Fetched from
    // live data (see /shopify/edd-filter-options) rather than hardcoded, so an option
    // never appears that no row can match. Cached server-side for 6h.
    const [shipmentFilterOptions, setShipmentFilterOptions] = useState<{
        courier_partner: string[]; pickup_hub: string[]; current_status: string[]; risk: string[];
    }>({ courier_partner: [], pickup_hub: [], current_status: [], risk: [] });

    // Outbound calling state
    const [outboundConfig, setOutboundConfig] = useState<OutboundCallConfig | null>(null);
    const [isCallModalOpen, setIsCallModalOpen] = useState(false);
    // order_uuid rides along on the shipment screens (EDD / Failed Delivery / RTO),
    // where a row IS an order, so the call is recorded against the right one.
    const [selectedCallLead, setSelectedCallLead] = useState<{ uuid: string; name: string; phone: string; orderUuid?: string | null } | null>(null);
    const { isCallInProgress, setCallInProgress } = useCallingState();
    const [copiedPhone, setCopiedPhone] = useState<string | null>(null);

    // Use shared users context to avoid duplicate API calls
    const { users: usersFromContext, ownerOptions } = useUsers();
    const users = usersFromContext as any[];

    // Get current user to check permissions
    const { userData } = useUser();
    const canExport = userData?.data?.role?.lead_scope !== 'assigned';

    // Filter popover state
    const [filterPopoverColumn, setFilterPopoverColumn] = useState<string | null>(null);
    const [filterPopoverPosition, setFilterPopoverPosition] = useState<{ top: number; left: number } | null>(null);

    // Bulk reminder states
    const [reminderTitle, setReminderTitle] = useState<string>('');
    const [reminderDateTime, setReminderDateTime] = useState<string>('');
    const [reminderAlertBefore, setReminderAlertBefore] = useState<string>('15 min');

    // Refs for scroll handling
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLTableSectionElement>(null);

    // Check if current user is a sales rep (hide owner filter for sales reps)
    const currentUserRole = getCurrentUserRole();
    const isSalesRep = currentUserRole === ROLES.SALES_REP ||
                       currentUserRole === ROLES.SENIOR_SALES_REP ||
                       currentUserRole === ROLES.JUNIOR_SALES_REP;
    const [columnsLoaded, setColumnsLoaded] = useState(false);

    // Disposition filter options (loaded from API)
    const [dispositionL1Options, setDispositionL1Options] = useState<{ opt_id: number; opt_name: string }[]>([]);
    const [dispositionL2Options, setDispositionL2Options] = useState<{ opt_id: number; opt_name: string }[]>([]);
    const [dispositionL3Options, setDispositionL3Options] = useState<{ opt_id: number; opt_name: string }[]>([]);

    // Persistent storage keys
    const USER_COLUMN_PREFERENCES_KEY = 'crm_user_column_preferences';
    // Version key to force reset preferences when defaults change
    // Increment this when default columns change to reset all users to new defaults
    const COLUMN_PREFERENCES_VERSION = 12; // v12: Order Source added to EDD + NDR

    // Helper functions for persistent storage
    const saveColumnPreferencesToStorage = (visibleCols: Set<string>) => {
        try {
            const preferences = {
                visibleColumns: Array.from(visibleCols),
                timestamp: Date.now(),
                version: COLUMN_PREFERENCES_VERSION
            };
            localStorage.setItem(USER_COLUMN_PREFERENCES_KEY, JSON.stringify(preferences));
        } catch (error) {
            console.error('❌ Error saving column preferences to localStorage:', error);
        }
    };

    const loadColumnPreferencesFromStorage = (): { columns: Set<string> | null; versionReset: boolean } => {
        try {
            const stored = localStorage.getItem(USER_COLUMN_PREFERENCES_KEY);
            if (!stored) return { columns: null, versionReset: false };

            const preferences = JSON.parse(stored);

            // Check version - if outdated, return null AND flag to skip database preferences
            if (!preferences.version || preferences.version < COLUMN_PREFERENCES_VERSION) {
                localStorage.removeItem(USER_COLUMN_PREFERENCES_KEY);
                return { columns: null, versionReset: true }; // Flag to skip database and use new defaults
            }

            return { columns: new Set(preferences.visibleColumns), versionReset: false };
        } catch (error) {
            console.error('❌ Error loading column preferences from localStorage:', error);
            return { columns: null, versionReset: false };
        }
    };

    // Load courier / hub / status dropdown options — EDD and NDR screens only.
    useEffect(() => {
        if (!mode.isShipmentScreen) return;
        let cancelled = false;
        (async () => {
            try {
                // Scoped to this screen: the two share the endpoint but not the
                // option lists — an RTO or Lost status is a real Failed Delivery
                // value and can never appear on EDD.
                const issueType = mode.isFailedDelivery ? 'ndr' : 'edd';
                const res: any = await optimizedApi.get(`/shopify/edd-filter-options?screen=${issueType}`);
                const d = res?.data as {
                    courier_partner?: string[]; pickup_hub?: string[]; current_status?: string[]; risk?: string[];
                } | null;
                if (cancelled || !d) return;
                setShipmentFilterOptions({
                    courier_partner: d.courier_partner || [],
                    pickup_hub: d.pickup_hub || [],
                    current_status: d.current_status || [],
                    risk: d.risk || [],
                });
            } catch {
                // Non-fatal: the dropdowns stay empty, every other filter keeps working.
            }
        })();
        return () => { cancelled = true; };
    }, [apiEndpoint]);

    // No longer need the mutation hook

    // Load outbound config on mount (from cache or API)
    useEffect(() => {
        const loadOutboundConfig = async () => {
            // First try to get from cache
            const cachedConfig = getCachedOutboundConfig();
            if (cachedConfig) {
                setOutboundConfig(cachedConfig);
                return;
            }

            // If not cached, fetch from API
            try {
                const response = await apiRequest('/api/outbound-call/config', {
                    method: 'GET'
                });

                if (response.success && response.data && response.data.uuid) {
                    const config: OutboundCallConfig = {
                        uuid: response.data.uuid,
                        provider: response.data.provider,
                        provider_name: response.data.provider_name,
                        is_active: response.data.is_active
                    };
                    // Cache the config
                    cacheOutboundConfig(config);
                    setOutboundConfig(config);
                } else {
                    // No config exists
                    setOutboundConfig(null);
                }
            } catch (error) {
                console.error('Failed to fetch outbound config:', error);
                setOutboundConfig(null);
            }
        };

        loadOutboundConfig();
    }, []);

    // Load disposition options for filters
    useEffect(() => {
        const loadDispositionOptions = async () => {
            try {
                const bucketType: BucketType = mode.bucketType;

                const response = await apiRequest(`/dispositions/tree/${bucketType}`, {
                    method: 'GET'
                });

                if (response.success && response.data) {
                    const tree = response.data as DispositionTree;

                    // Map L1 options to FSelect format
                    const l1Opts = tree.l1Options.map((opt: DispositionOption) => ({
                        opt_id: opt.option_id,
                        opt_name: opt.value
                    }));
                    setDispositionL1Options(l1Opts);

                    // Collect ALL L2 options from all L1 parents
                    const allL2: { opt_id: number; opt_name: string }[] = [];
                    Object.values(tree.l2ByL1).forEach((l2Options: DispositionOption[]) => {
                        l2Options.forEach((opt: DispositionOption) => {
                            // Avoid duplicates by checking if already exists
                            if (!allL2.some(o => o.opt_id === opt.option_id)) {
                                allL2.push({ opt_id: opt.option_id, opt_name: opt.value });
                            }
                        });
                    });
                    setDispositionL2Options(allL2);

                    // Collect ALL L3 options from all L2 parents
                    const allL3: { opt_id: number; opt_name: string }[] = [];
                    Object.values(tree.l3ByL2).forEach((l3Options: DispositionOption[]) => {
                        l3Options.forEach((opt: DispositionOption) => {
                            if (!allL3.some(o => o.opt_id === opt.option_id)) {
                                allL3.push({ opt_id: opt.option_id, opt_name: opt.value });
                            }
                        });
                    });
                    setDispositionL3Options(allL3);

                }
            } catch (error) {
                console.error('❌ Failed to load disposition options:', error);
            }
        };

        loadDispositionOptions();
    }, [apiEndpoint]);

    // Step 1: Fetch columns on mount
    useEffect(() => {
        const fetchColumns = async () => {
            try {
                const response = await ColumnsService.getUserColumns();
                
                if (response?.success && Array.isArray(response.columns)) {
                    const cols = response.columns;
                    
                    // Add only computed columns that are not in the API response
                    // (Tracking columns now come from backend API with is_tracking_field flag)
                    const additionalColumns: Column[] = [
                        { col_name: 'is_qualified', col_label: 'Qualified', col_type: 'boolean', is_custom: false, col_active: 1, user_column: false },
                        { col_name: 'updated_date', col_label: 'Updated Date', col_type: 'dateTime', is_custom: false, col_active: 1, user_column: true },
                        { col_name: 'customer_type', col_label: 'Customer Type', col_type: 'text', is_custom: false, col_active: 1, user_column: true },
                        { col_name: 'shopify_customer_id', col_label: 'Shopify Customer ID', col_type: 'text', is_custom: false, col_active: 1, user_column: true },
                        { col_name: 'product_info', col_label: 'Product Information', col_type: 'text', is_custom: false, col_active: 1, user_column: true },
                        { col_name: 'address', col_label: 'Address', col_type: 'text', is_custom: false, col_active: 1, user_column: true },
                    ];

                    // Check if these columns already exist in API response (avoid duplicates)
                    const existingColNames = new Set(cols.map(c => c.col_name));
                    const columnsToAdd = additionalColumns.filter(c => !existingColNames.has(c.col_name));

                    // Combine API columns with non-duplicate additional columns
                    const allColumns = [...cols, ...columnsToAdd];
                    
                    // Sort columns according to standard order:
                    // 1. Phone (first, always visible)
                    // 2. All CRM columns in col_order ASC (including custom columns)
                    // 3. Tracking columns (UTM, FB, LinkedIn)
                    // 4. Date columns (created_date, updated_date) ALWAYS at end
                    const dateColumnsAtEnd = ['created_date', 'updated_date'];
                    // For reorder pages, define specific column order: phone, name, email, order_date
                    const reorderColumnOrder = ['phone', 'name', 'email'];
                    const isReorderPage = mode.isReorder;

                    const sortedColumns = allColumns.sort((a, b) => {
                        // Phone always comes first
                        if (a.col_name === 'phone') return -1;
                        if (b.col_name === 'phone') return 1;

                        // For reorder pages, enforce specific order for key columns
                        if (isReorderPage) {
                            const aReorderIndex = reorderColumnOrder.indexOf(a.col_name);
                            const bReorderIndex = reorderColumnOrder.indexOf(b.col_name);
                            if (aReorderIndex !== -1 && bReorderIndex !== -1) {
                                return aReorderIndex - bReorderIndex;
                            }
                            if (aReorderIndex !== -1) return -1;
                            if (bReorderIndex !== -1) return 1;
                        }

                        // Date columns always at the end
                        const aIsDateCol = dateColumnsAtEnd.includes(a.col_name);
                        const bIsDateCol = dateColumnsAtEnd.includes(b.col_name);
                        if (aIsDateCol && !bIsDateCol) return 1;
                        if (!aIsDateCol && bIsDateCol) return -1;
                        if (aIsDateCol && bIsDateCol) {
                            // Sort date columns in specific order
                            return dateColumnsAtEnd.indexOf(a.col_name) - dateColumnsAtEnd.indexOf(b.col_name);
                        }

                        // Categorize columns using is_tracking_field flag
                        const aIsTracking = a.is_tracking_field === true;
                        const bIsTracking = b.is_tracking_field === true;

                        // Group ordering: CRM columns -> Tracking columns
                        // Push tracking columns to the end (but before date columns)
                        if (!aIsTracking && bIsTracking) return -1;
                        if (aIsTracking && !bIsTracking) return 1;

                        // Within tracking columns, sort by category, then by label
                        if (aIsTracking && bIsTracking) {
                            if (a.category !== b.category) {
                                // Order: UTM -> Facebook -> LinkedIn
                                const categoryOrder: Record<string, number> = {
                                    'UTM Tracking': 1,
                                    'Facebook Tracking': 2,
                                    'LinkedIn Tracking': 3,
                                };
                                return (categoryOrder[a.category || ''] || 99) - (categoryOrder[b.category || ''] || 99);
                            }
                            // Same category, sort alphabetically by label
                            return (a.col_label || '').localeCompare(b.col_label || '');
                        }

                        // Within CRM columns, sort by col_order
                        if (!aIsTracking && !bIsTracking) {
                            const aOrder = a.col_order ?? 999;
                            const bOrder = b.col_order ?? 999;
                            if (aOrder !== bOrder) {
                                return aOrder - bOrder;
                            }

                            // Fallback to alphabetical for columns without col_order
                            return (a.col_label || '').localeCompare(b.col_label || '');
                        }

                        // Within same group (UTM or Social), sort alphabetically
                        return a.col_name.localeCompare(b.col_name);
                    });
                    
                    // Load column preferences from localStorage or use database selection
                    const { columns: storedPreferences, versionReset } = loadColumnPreferencesFromStorage();
                    let initialVisible: Set<string>;

                    // Default columns: Phone, Name, Lead Status 1/2/3, Email, Label, Owner, Call Status, Call Duration, Created Date, Updated Date
                    const essentialColumns = ['phone', 'name', 'disposition_l1', 'disposition_l2', 'disposition_l3', 'email', 'label', 'product_info', 'address', 'owner', 'last_call_status', 'last_call_duration', 'created_date', 'updated_date'];

                    // Check if this is a first-time user (from API response)
                    const isFirstTimeUser = (response as any).is_first_time === true;

                    if (storedPreferences && storedPreferences.size > 0) {
                        // Use stored preferences from localStorage (highest priority)
                        initialVisible = storedPreferences;
                    } else if (versionReset || isFirstTimeUser) {
                        // Version was reset OR first-time user - use new defaults
                        initialVisible = new Set(essentialColumns);
                        // Save to localStorage immediately
                        saveColumnPreferencesToStorage(initialVisible);
                    } else {
                        // Existing user with database preferences
                        const userSelectedColumns = sortedColumns
                            .filter(col => col.user_column === true)
                            .map(col => col.col_name);

                        if (userSelectedColumns.length > 0) {
                            // User has saved preferences in database
                            initialVisible = new Set(userSelectedColumns);

                            // Save to localStorage for next time
                            saveColumnPreferencesToStorage(initialVisible);
                        } else {
                            // Fallback to essential columns
                            initialVisible = new Set(essentialColumns);
                        }
                    }

                    // IMPORTANT: Always ensure phone, owner, disposition, and call log columns are visible (required columns)
                    initialVisible.add('phone');
                    initialVisible.add('owner');
                    initialVisible.add('disposition_l1');
                    initialVisible.add('disposition_l2');
                    initialVisible.add('disposition_l3');
                    initialVisible.add('last_call_status');
                    initialVisible.add('last_call_duration');
                    initialVisible.add('last_abc_date');
                    initialVisible.add('profile_captured');
                    // GoKwik COD risk score -- available on every lead screen, not just
                    // EDD/NDR/RTO (renders '—' where the lead has no placed order yet).
                    initialVisible.add('risk');

                    // Remove deprecated status column (replaced by Lead Status 1/2/3)
                    initialVisible.delete('status');

                    // Per-screen column deltas, declared in bucketMode.ts. Removals
                    // run first so an add always wins — the shipment screens hide
                    // Address but show Product Information, for instance.
                    mode.removeColumns.forEach(col => initialVisible.delete(col));
                    mode.addColumns.forEach(col => initialVisible.add(col));

                    // Set the columns and mark as loaded. The legacy Status field is
                    // out of the picker entirely: nothing updates it after intake, so it
                    // reads "New Lead" on leads agents have already called (Sep 2026).
                    setColumns(sortedColumns.filter(col => col.col_name !== 'status'));
                    setVisibleColumns(initialVisible);
                    setColumnsLoaded(true);
                } else {
                    console.error('❌ Invalid columns response:', response);
                }
            } catch (error) {
                console.error('❌ Error fetching columns:', error);
            }
        };
        
        fetchColumns();
    }, []); // Empty dependency array - run only once on mount

    // Step 2: Fetch leads when columns are loaded or when filters/pagination changes
    const fetchLeads = useCallback(async () => {
        // Only fetch if columns are loaded
        if (!columnsLoaded) {
            return;
        }
        
        
        // Map updated_date to last_updated_date for backend compatibility
        const backendSortColumn = sortColumn === 'updated_date' ? 'last_updated_date' : sortColumn;

        // Determine agent_created filter value:
        // - agentCreatedOnly: true means only show agent-created leads (agent_created: true)
        // - excludeAgentCreated: true means exclude agent-created leads (agent_created: false)
        // - Otherwise: undefined (no filter)
        let agentCreatedValue: boolean | undefined = undefined;
        if (agentCreatedOnly) {
            agentCreatedValue = true;
        } else if (excludeAgentCreated) {
            agentCreatedValue = false;
        }

        const payload: LeadsPayload & { agent_created?: boolean; lead_bucket_filter?: string; max_days_future?: number } = {
            page: pagination.currentPage,
            limit: pagination.itemsPerPage,
            sort_by: backendSortColumn,
            sort_order: sortDirection,
            primary_filters: {},
            custom_filters: {},
            agent_created: agentCreatedValue, // Filter for agent-created leads
            ...(leadBucketFilter && { lead_bucket_filter: leadBucketFilter }), // e.g. 'phone' bucket tab
            ...(maxDaysFuture > 0 && { max_days_future: maxDaysFuture }), // For reorder-leads: include future days
        };

        // Add search term to payload
        if (searchTerm && searchTerm.trim()) {
            payload.primary_filters!['search'] = searchTerm.trim();
        }

        // Create a map of column names to column metadata for quick lookup
        const columnMap = new Map<string, Column>();
        columns.forEach(col => {
            columnMap.set(col.col_name, col);
        });

        // Add filters to payload
        Object.entries(activeFilters).forEach(([key, filterItem]) => {
            // Type guard to ensure filterItem has the expected structure
            const filter = filterItem as CustomFieldFilter;

            if (!filter) return;

            // Map updated_date to last_updated_date for backend compatibility
            const backendKey = key === 'updated_date' ? 'last_updated_date' : key;

            // Look up column metadata to determine if it's a custom field
            const columnMetadata = columnMap.get(key);
            const isCustomField = columnMetadata?.is_custom === true;
            const columnUuid = columnMetadata?.uuid;

            // Handle numeric range filters (min/max instead of value)
            if (filter.type === 'numberRange') {
                const rangeFilter: any = {};
                if (filter.min !== undefined && filter.min !== '') rangeFilter.min = filter.min;
                if (filter.max !== undefined && filter.max !== '') rangeFilter.max = filter.max;
                if (Object.keys(rangeFilter).length > 0) {
                    payload.primary_filters![backendKey] = rangeFilter;
                }
                return;
            }

            // Handle date/dateTime filters (they have from/to instead of value)
            if (filter.type === 'date' || filter.type === 'dateTime') {
                // Only send date filters with non-empty values
                const dateFilter: any = {};
                if (filter.from && filter.from !== '') dateFilter.from = filter.from;
                if (filter.to && filter.to !== '') dateFilter.to = filter.to;

                // Only add to payload if at least one date is set
                if (Object.keys(dateFilter).length > 0) {
                    if (isCustomField && columnUuid) {
                        // Custom field date filter - add to custom_filters with column UUID
                        payload.custom_filters![columnUuid] = dateFilter;
                    } else {
                        // Primary field date filter
                        payload.primary_filters![backendKey] = dateFilter;
                    }
                }
                return;
            }

            // For non-date filters, check if value exists
            if (filter.value === undefined || filter.value === null || filter.value === '') {
                return;
            }

            // Handle owner filter - send UUIDs to the backend
            if (key === 'owner') {
                if (Array.isArray(filter.value) && filter.value.length > 0) {
                    // Send owner UUIDs to the backend
                    payload.primary_filters!['owner_uuids'] = filter.value;
                }
                return;
            }

            // Handle is_qualified filter - convert string to number
            if (key === 'is_qualified') {
                payload.primary_filters![key] = parseInt(String(filter.value));
                return;
            }

            // Handle select filters
            if (filter.type === 'select' && Array.isArray(filter.value) && filter.value.length > 0) {
                if (isCustomField && columnUuid) {
                    // Custom field select - add to custom_filters with column UUID
                    payload.custom_filters![columnUuid] = filter.value;
                } else {
                    // Primary field select - add to primary_filters
                    payload.primary_filters![backendKey] = filter.value;
                    // Debug log for disposition filters
                    if (key.startsWith('disposition_')) {
                    }
                }
            }
            // Handle text filters
            else if (!Array.isArray(filter.value) && filter.value !== '') {
                if (isCustomField && columnUuid) {
                    // Custom field text - add to custom_filters with column UUID
                    payload.custom_filters![columnUuid] = filter.value;
                } else {
                    // Primary field text - add to primary_filters
                    payload.primary_filters![backendKey] = filter.value;
                }
            }
        });

        try {
            setIsLoading(true);
            const result = await optimizedApi.post(apiEndpoint, payload);
            
            if (result.success) {
                const leadsData = Array.isArray(result.data) ? result.data : [];

                // Clear selections when data changes to prevent stale state
                setSelectedRows(new Set());
                setSelectAll(false);

                // No need for client-side owner filtering anymore since it's handled by backend
                setLeads(leadsData);
                setPagination(prev => ({
                    ...prev,
                    totalPages: result.totalPages || 1,
                    totalItems: result.total || 0
                }));

                // Store date range info from API response
                if (result.dateRange) {
                    setDateRange({
                        from: result.dateRange.from,
                        to: result.dateRange.to,
                        isDefault: result.dateRange.isDefault ?? false
                    });
                }
            } else {
            }
        } catch (error) {
            console.error('❌ Error fetching leads:', error);
        } finally {
            setIsLoading(false);
        }
    }, [columnsLoaded, pagination.currentPage, pagination.itemsPerPage, sortColumn, sortDirection, activeFilters, searchTerm, agentCreatedOnly, excludeAgentCreated, leadBucketFilter, maxDaysFuture, mode.bucketType, isSalesRep]);

    // Trigger leads fetch when columns are loaded or dependencies change
    useEffect(() => {
        if (columnsLoaded) {
            fetchLeads();
        }
    }, [columnsLoaded, fetchLeads]);

    // Expose refresh method to parent component via ref
    useImperativeHandle(ref, () => ({
        refreshLeads: async () => {
            await fetchLeads();
        }
    }), [fetchLeads]);

    // Handle column sorting
    const handleSort = (columnName: string) => {
        if (sortColumn === columnName) {
            setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortColumn(columnName);
            setSortDirection('ASC');
        }
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    // Handle filter changes (immediate input changes)
    const handleFilterChange = (columnName: string, value: any, type: 'text' | 'select' | 'date' | 'dateTime' | 'numberRange') => {

        if (type === 'numberRange') {
            // Merges {min} / {max} the same way date ranges merge {from} / {to}, so
            // typing one bound doesn't wipe the other.
            setTempFilters(prev => {
                const prevFilter = prev[columnName] as CustomFieldFilter | undefined;
                return {
                    ...prev,
                    [columnName]: { ...(prevFilter || {}), ...value, type }
                };
            });
        } else if (type === 'date' || type === 'dateTime') {
            // Handle date range filters
            setTempFilters(prev => {
                const prevFilter = prev[columnName] as CustomFieldFilter | undefined;
                return {
                    ...prev,
                    [columnName]: {
                        ...(prevFilter || {}),
                        ...value,
                        type
                    }
                };
            });
        } else if (type === 'select') {
            // Handle multi-select filters
            const newFilterValue = { value: Array.isArray(value) ? value : [value].filter(Boolean), type };
            setTempFilters(prev => ({
                ...prev,
                [columnName]: newFilterValue
            }));
        } else {
            setTempFilters(prev => ({
                ...prev,
                [columnName]: { value, type }
            }));
        }
    };

    // Apply filters (when user confirms)
    const applyFilters = () => {
        setActiveFilters({ ...tempFilters });
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    // Toggle filter popover
    const handleFilterIconClick = (columnName: string, event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation(); // Prevent sort from triggering

        if (filterPopoverColumn === columnName) {
            // Close if already open
            setFilterPopoverColumn(null);
            setFilterPopoverPosition(null);
        } else {
            // Open popover for this column
            const rect = event.currentTarget.getBoundingClientRect();
            const popoverWidth = 320; // max-width of popover
            const viewportWidth = window.innerWidth;

            // Calculate left position, ensuring popover stays within viewport
            let left = rect.left + window.scrollX;
            if (left + popoverWidth > viewportWidth) {
                // Align to right edge of button instead
                left = rect.right + window.scrollX - popoverWidth;
            }

            setFilterPopoverColumn(columnName);
            setFilterPopoverPosition({
                top: rect.bottom + window.scrollY + 4,
                left: Math.max(10, left) // Ensure at least 10px from left edge
            });
        }
    };

    // Close filter popover
    const closeFilterPopover = () => {
        setFilterPopoverColumn(null);
        setFilterPopoverPosition(null);
    };

    // Check if column has active filter
    const hasActiveFilter = (columnName: string): boolean => {
        const filter = activeFilters[columnName] as CustomFieldFilter | undefined;
        if (!filter) return false;

        if (filter.type === 'date' || filter.type === 'dateTime') {
            return !!(filter.from || filter.to);
        }
        if (filter.type === 'numberRange') {
            // 0 is a legitimate bound ("dispatched today"), so test for presence, not truthiness.
            return (filter.min !== undefined && filter.min !== '') || (filter.max !== undefined && filter.max !== '');
        }
        if (Array.isArray(filter.value)) {
            return filter.value.length > 0;
        }
        return !!filter.value;
    };

    // Check if there are filters to apply
    const hasFiltersToApply = useMemo(() => {
        return Object.keys(tempFilters).some(key => {
            const tempValue = tempFilters[key] as CustomFieldFilter | undefined;
            const activeValue = activeFilters[key] as CustomFieldFilter | undefined;

            // Handle array values (multi-select)
            if (Array.isArray(tempValue?.value) && Array.isArray(activeValue?.value)) {
                return JSON.stringify(tempValue.value.sort()) !== JSON.stringify(activeValue.value.sort());
            }

            // Handle date range values
            if (tempValue?.type === 'date' || tempValue?.type === 'dateTime') {
                return tempValue?.from !== activeValue?.from || tempValue?.to !== activeValue?.to;
            }

            // Handle numeric range values
            if (tempValue?.type === 'numberRange') {
                return tempValue?.min !== activeValue?.min || tempValue?.max !== activeValue?.max;
            }

            // Handle other values
            return tempValue?.value !== activeValue?.value;
        }) || Object.keys(tempFilters).length !== Object.keys(activeFilters).length;
    }, [tempFilters, activeFilters]);

    // Clear all filters
    const clearFilters = () => {
        setActiveFilters({});
        setTempFilters({});
        setSearchTerm('');
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    // Handle search
    const handleSearch = useCallback((term: string) => {
        setSearchTerm(term);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, []);

    // Convert pipeline filters to leads table format
    const convertPipelineFiltersToLeadsFormat = useCallback((pipelineFilters: any) => {
        const leadsFilters: FilterState = {};

        const textFields = ['phone', 'name', 'email'];
        const dateFields = ['created_date', 'updated_date', 'last_updated_date'];
        const dateFromToFields = ['last_abc_date', 'reorder_due_date']; // fields with {from, to} shape

        Object.entries(pipelineFilters).forEach(([key, value]: [string, any]) => {
            if (value === undefined || value === null || value === '') return;

            // Text filters
            if (textFields.includes(key) && typeof value === 'string') {
                leadsFilters[key] = { value, type: 'text' };
            }
            // startDate/endDate shape date filters
            else if (dateFields.includes(key) && typeof value === 'object' && (value.startDate || value.endDate)) {
                leadsFilters[key] = { value, type: 'date', from: value.startDate, to: value.endDate };
            }
            // from/to shape date filters (last_abc_date)
            else if (dateFromToFields.includes(key) && typeof value === 'object' && (value.from || value.to)) {
                leadsFilters[key] = { value, type: 'date', from: value.from, to: value.to };
            }
            // Disposition L1/L2/L3 — numeric string select
            else if (['disposition_l1', 'disposition_l2', 'disposition_l3'].includes(key) && value) {
                leadsFilters[key] = { value: [String(value)], type: 'select' };
            }
            // Select filters (arrays)
            else if (Array.isArray(value) && value.length > 0) {
                leadsFilters[key] = { value, type: 'select' };
            }
            // Generic text fallback for any other string field from FiltersModal
            else if (typeof value === 'string' && value.trim() !== '') {
                leadsFilters[key] = { value, type: 'text' };
            }
        });

        return leadsFilters;
    }, []);

    // Convert leads filters to pipeline format
    const convertLeadsFiltersToPipelineFormat = useCallback((leadsFilters: FilterState) => {
        const pipelineFilters = {
            textFilters: {} as any,
            dateFilters: {} as any,
            columnFilters: {} as any
        };

        Object.entries(leadsFilters).forEach(([key, filterItem]) => {
            const filter = filterItem as CustomFieldFilter;
            if (!filter) return;

            if (filter.type === 'text') {
                pipelineFilters.textFilters[key] = filter.value;
            } else if (filter.type === 'date' || filter.type === 'dateTime') {
                pipelineFilters.dateFilters[key] = {
                    from: filter.from,
                    to: filter.to
                };
            } else if (filter.type === 'select') {
                pipelineFilters.columnFilters[key] = filter.value;
            }
        });

        return pipelineFilters;
    }, []);

    // Convert the raw activeFilters state ({value, type, from, to}) into the
    // backend payload shape ({primary_filters, custom_filters} with bare values).
    // Mirrors the conversion done in the listing fetch so the export sends the
    // same filter shape the backend expects (avoids e.g. disposition_l1 arriving
    // as {value:[...], type:"select"}).
    const convertActiveFiltersToBackend = useCallback((filters: FilterState) => {
        const primary_filters: Record<string, any> = {};
        const custom_filters: Record<string, any> = {};

        const columnMap = new Map<string, Column>();
        columns.forEach(col => columnMap.set(col.col_name, col));

        Object.entries(filters).forEach(([key, filterItem]) => {
            const filter = filterItem as CustomFieldFilter;
            if (!filter) return;

            const backendKey = key === 'updated_date' ? 'last_updated_date' : key;
            const columnMetadata = columnMap.get(key);
            const isCustomField = columnMetadata?.is_custom === true;
            const columnUuid = columnMetadata?.uuid;

            if (filter.type === 'numberRange') {
                const rangeFilter: any = {};
                if (filter.min !== undefined && filter.min !== '') rangeFilter.min = filter.min;
                if (filter.max !== undefined && filter.max !== '') rangeFilter.max = filter.max;
                if (Object.keys(rangeFilter).length > 0) primary_filters[backendKey] = rangeFilter;
                return;
            }

            if (filter.type === 'date' || filter.type === 'dateTime') {
                const dateFilter: any = {};
                if (filter.from && filter.from !== '') dateFilter.from = filter.from;
                if (filter.to && filter.to !== '') dateFilter.to = filter.to;
                if (Object.keys(dateFilter).length > 0) {
                    if (isCustomField && columnUuid) custom_filters[columnUuid] = dateFilter;
                    else primary_filters[backendKey] = dateFilter;
                }
                return;
            }

            if (filter.value === undefined || filter.value === null || filter.value === '') return;

            if (key === 'owner') {
                if (Array.isArray(filter.value) && filter.value.length > 0) {
                    primary_filters['owner_uuids'] = filter.value;
                }
                return;
            }

            if (key === 'is_qualified') {
                primary_filters[key] = parseInt(String(filter.value));
                return;
            }

            if (filter.type === 'select' && Array.isArray(filter.value) && filter.value.length > 0) {
                if (isCustomField && columnUuid) custom_filters[columnUuid] = filter.value;
                else primary_filters[backendKey] = filter.value;
            } else if (!Array.isArray(filter.value) && filter.value !== '') {
                if (isCustomField && columnUuid) custom_filters[columnUuid] = filter.value;
                else primary_filters[backendKey] = filter.value;
            }
        });

        // Export what the list shows.
        return { primary_filters, custom_filters };
    }, [columns, isSalesRep]);

    // Handle advanced filters apply
    const handleAdvancedFiltersApply = useCallback((pipelineFilters: any) => {
        const convertedFilters = convertPipelineFiltersToLeadsFormat(pipelineFilters);
        setActiveFilters(convertedFilters);
        setTempFilters(convertedFilters);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        setIsAdvancedFiltersOpen(false);
    }, [convertPipelineFiltersToLeadsFormat]);

    // Handle row selection
    const handleRowSelect = (leadUuid: string, checked: boolean) => {
        const newSelection = new Set(selectedRows);
        if (checked) {
            newSelection.add(leadUuid);
        } else {
            newSelection.delete(leadUuid);
        }
        setSelectedRows(newSelection);
        setSelectAll(newSelection.size === leads.length && leads.length > 0);
        
        onRowSelect?.(Array.from(newSelection));
    };

    // Handle select all — row identity comes from this screen's rowIdFields (see bucketMode.ts)
    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const allIds = new Set(leads.map(lead => getRowId(mode, lead as any)));
            setSelectedRows(allIds);
            onRowSelect?.(Array.from(allIds));
        } else {
            setSelectedRows(new Set());
            onRowSelect?.([]);
        }
        setSelectAll(checked);
    };

    // Handle pagination
    const handlePageChange = (page: number) => {
        setPagination(prev => ({ ...prev, currentPage: page }));
    };

    const handleItemsPerPageChange = (itemsPerPage: number) => {
        setPagination(prev => ({
            ...prev,
            itemsPerPage,
            currentPage: 1
        }));
    };

    // Copy phone number to clipboard
    const handleCopyPhone = async (phone: string, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent opening lead details
        try {
            await navigator.clipboard.writeText(phone);
            setCopiedPhone(phone);
            setTimeout(() => setCopiedPhone(null), 2000); // Reset after 2 seconds
        } catch (error) {
            console.error('Failed to copy phone:', error);
        }
    };

    // Outbound calling handlers
    const handleInitiateCall = (lead: Lead, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent opening lead details

        if (isCallInProgress) {
            showToast('Another call is already in progress', 'error');
            return;
        }

        const phoneNumber = getCellValue(lead, 'phone');
        if (!phoneNumber) {
            showToast('No phone number available for this lead', 'error');
            return;
        }

        setSelectedCallLead({
            uuid: lead.uuid,
            name: getCellValue(lead, 'name') || 'Unknown',
            phone: phoneNumber,
            orderUuid: (lead as { order_uuid?: string | null }).order_uuid ?? null
        });
        setIsCallModalOpen(true);
        setCallInProgress(true);
    };

    const handleCloseCallModal = () => {
        setIsCallModalOpen(false);
        setSelectedCallLead(null);
        setCallInProgress(false);
    };

    // Get cell value with proper formatting
    const getCellValue = useCallback((lead: Lead, columnName: string): string => {
        // ~2% of stored lead names carry a literal "None" token from a first/last
        // join that had a Python None on one side ("Praveen None", "None None").
        // Cleaned here because every cell in this table renders through this one
        // function, so the name column is fixed wherever it is shown or exported.
        if (columnName === 'name') {
            // 'N/A' matches what this function already returns for a missing
            // value, so a name that cleans away to nothing reads the same as a
            // name that was never set — no new empty-state to explain.
            return displayName((lead as any).name) || 'N/A';
        }
        // Handle special computed columns
        if (columnName === 'owner') {
            // First check for direct owner field (from reorder-leads API)
            if ((lead as any).owner) {
                return (lead as any).owner;
            }
            // Fall back to assignments array (from leads API)
            const assignments = (lead as any).assignments;
            if (assignments && Array.isArray(assignments) && assignments.length > 0) {
                // Find the active assignment
                const activeAssignment = assignments.find((assignment: any) => assignment.is_active === true);
                if (activeAssignment && activeAssignment.user && activeAssignment.user.userName) {
                    return activeAssignment.user.userName;
                }
            }
            return '--';
        }
        if (columnName === 'last_remark') {
            // Get the latest remark from the remarks array, filtering for normal, user, or audio remarks
            const remarks = (lead as any).remarks;
            if (remarks && Array.isArray(remarks) && remarks.length > 0) {
                // Filter for normal, user, or audio remarks and find the most recent one
                const filteredRemarks = remarks.filter((remark: any) => {
                    const remarkType = remark.remark_type;

                    // For audio remarks, also check if source is not empty and not "None"
                    if (remarkType === 'audio') {
                        return remark.source && remark.source !== 'None';
                    }

                    return remarkType === 'normal' || remarkType === 'user';
                });

                if (filteredRemarks.length > 0) {
                    // Sort by date to get the most recent (try multiple date fields)
                    const sortedRemarks = filteredRemarks.sort((a: any, b: any) => {
                        // Try different date fields that might exist
                        const dateA = new Date(a.date_remark || a.formattedDate || a.createdAt || a.created_at || 0);
                        const dateB = new Date(b.date_remark || b.formattedDate || b.createdAt || b.created_at || 0);
                        return dateB.getTime() - dateA.getTime(); // Most recent first
                    });

                    const latestRemark = sortedRemarks[0];
                    if (latestRemark.remark_type === 'audio') {
                        return '🎵 Audio Recording';
                    }
                    const remarkText = latestRemark.remark || 'No remark text';
                    // Truncate long remarks for table display
                    return remarkText.length > 50 ? remarkText.substring(0, 50) + '...' : remarkText;
                }
            }
            return 'No remarks';
        }

        // Address — combine lead address fields into a single readable line
        if (columnName === 'address') {
            const parts = [
                (lead as any).address_1,
                (lead as any).address_2,
                (lead as any).city,
                (lead as any).province,
                (lead as any).zip,
            ].filter((p) => p && String(p).trim() !== '');
            return parts.length > 0 ? parts.join(', ') : '—';
        }
        // Product Information — summarise cart/order line items
        if (columnName === 'product_info') {
            const items = (lead as any).lineItems;
            if (items && Array.isArray(items) && items.length > 0) {
                return items
                    .map((it: any) => {
                        const qty = it.quantity && it.quantity > 1 ? ` ×${it.quantity}` : '';
                        // Variant is the pack size — an order can carry the same product in two
                        // sizes, which would otherwise render as the same title twice.
                        const variant = it.title && it.variant_title ? ` (${it.variant_title})` : '';
                        return `${it.title || it.variant_title || 'Product'}${variant}${qty}`;
                    })
                    .join('; ');
            }
            return '—';
        }

        // Handle special display properties
        if (columnName === 'status') {
            return lead.statusDisplay || lead.statusOption?.opt_name || String(lead.status || 'N/A');
        }
        if (columnName === 'source') {
            return lead.sourceDisplay || lead.sourceOption?.opt_name || String(lead.source || 'N/A');
        }
        if (columnName === 'label') {
            return lead.labelDisplay || lead.labelOption?.opt_name || 'N/A';
        }
        if (columnName === 'customer_type') {
            return lead.customerTypeDisplay || lead.customerTypeOption?.opt_name || 'New';
        }
        // Handle disposition columns
        if (columnName === 'disposition_l1') {
            return (lead as any).disposition_l1_value || 'New Lead';
        }
        if (columnName === 'disposition_l2') {
            return (lead as any).disposition_l2_value || '--';
        }
        if (columnName === 'disposition_l3') {
            return (lead as any).disposition_l3_value || '--';
        }
        // Reorder-specific: status_text column (Due Today, Xd overdue, Due in Xd)
        if (columnName === 'status_text') {
            return (lead as any).status_text || 'N/A';
        }
        // Reorder-specific: format order_date nicely
        if (columnName === 'order_date') {
            const value = (lead as any).order_date;
            if (value && typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
                try {
                    return format(parseISO(value), 'MMM dd, yyyy');
                } catch {
                    return value;
                }
            }
            return value || 'N/A';
        }
        // Reorder-specific: format reorder_due_date nicely.
        //
        // A reorder row is one row per CUSTOMER, not per product — two products coming
        // due is one phone call. But the other due products used to vanish with the
        // de-duplication, so an agent pitched one and never learned a second was also
        // due. other_due_count carries them, and is shown here so the call can cover
        // everything at once. Full details are in other_due_products on the row.
        if (columnName === 'reorder_due_date') {
            const value = (lead as any).reorder_due_date;
            const alsoDue = Number((lead as any).other_due_count) || 0;
            const suffix = alsoDue > 0 ? ` · +${alsoDue} more due` : '';
            if (value && typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
                try {
                    return format(parseISO(value), 'MMM dd, yyyy') + suffix;
                } catch {
                    return value + suffix;
                }
            }
            return value ? String(value) + suffix : 'N/A';
        }
        // RTO-specific: format rto_date (failed_at)
        if (columnName === 'rto_date') {
            const value = (lead as any).failed_at;
            if (value) {
                try {
                    return format(parseISO(value), 'dd MMM yyyy, h:mm a');
                } catch {
                    return String(value);
                }
            }
            return '—';
        }
        // RTO-specific: payment gateway
        if (columnName === 'payment_gateway') {
            return (lead as any).payment_gateway || '—';
        }
        // EDD-specific: expected delivery date
        if (columnName === 'expected_delivery_date') {
            const value = (lead as any).expected_delivery_date;
            if (value && typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
                try {
                    return format(parseISO(value), 'MMM dd, yyyy');
                } catch {
                    return value;
                }
            }
            return value ? String(value) : 'Not yet available';
        }
        // EDD-specific: priority label
        if (columnName === 'edd_priority') {
            return (lead as any).priority || 'In Transit';
        }
        // Confirm Orders: when the courier collects it, why it's on the list, the
        // customer's record, and what is at stake.
        if (columnName === 'ships_by') {
            return (lead as any).ships_by || '—';
        }
        if (columnName === 'cod_tier') {
            return (lead as any).cod_tier || '—';
        }
        if (columnName === 'customer_history') {
            return (lead as any).customer_history || '—';
        }
        if (columnName === 'total_price') {
            const v = (lead as any).total_price;
            return typeof v === 'number' ? `₹${Math.round(v).toLocaleString('en-IN')}` : '—';
        }
        // EDD/Failed-Delivery: who placed the order. Agent orders carry a Sales-* tag
        // written by the CRM at creation; everything else came straight from the
        // storefront/GoKwik. Label is built server-side so the grid, the badge and the
        // CSV can never disagree about an agent's name.
        if (columnName === 'order_source') {
            return (lead as any).order_source_label || 'Direct';
        }
        // EDD-specific: current Clickpost status/location
        if (columnName === 'current_status') {
            const desc = (lead as any).current_status;
            const loc = (lead as any).current_location;
            if (!desc) return '—';
            return loc ? `${desc} — ${loc}` : desc;
        }
        // EDD/Failed-Delivery/RTO: GoKwik COD risk score. Blank for prepaid orders — GoKwik
        // only scores COD, so most reorder/prepaid rows never carry one.
        if (columnName === 'risk') {
            return (lead as any).risk || '—';
        }
        // All screens: how complete is this customer's health/demographic profile.
        if (columnName === 'profile_captured') {
            return (lead as any).profile_captured || 'Missing';
        }
        // EDD/Failed-Delivery-specific: days late (0 or missing = not overdue)
        if (columnName === 'days_late') {
            const days = (lead as any).days_late;
            return days && days > 0 ? `${days}d late` : '—';
        }
        // EDD Ageing — signed days to/from the courier-promised EDD. Negative days_to_edd
        // means the promise is already broken, so it reads as "Nd late"; positive counts
        // down to it. Blank when the courier hasn't given us an EDD at all.
        if (columnName === 'edd_ageing') {
            const days = (lead as any).days_to_edd as number | null | undefined;
            if (days === null || days === undefined) return '—';
            if (days < 0) return `${Math.abs(days)}d late`;
            if (days === 0) return 'Due today';
            return days === 1 ? 'in 1d' : `in ${days}d`;
        }
        // Failed-Delivery-specific: NDR reason from Clickpost
        if (columnName === 'ndr_reason') {
            return (lead as any).ndr_reason || '—';
        }
        // Failed-Delivery-specific: NDR 1/2/3 — the last three failed delivery attempts,
        // oldest first, so NDR 3 is always the most recent failure.
        if (columnName === 'ndr_1' || columnName === 'ndr_2' || columnName === 'ndr_3') {
            const attempt = getNdrAttempt(lead, columnName);
            if (!attempt) return '—';
            return [formatNdrDate(attempt.attempt_date), attempt.reason].filter(Boolean).join(' — ') || '—';
        }
        // EDD/Failed-Delivery: Shopify order number (e.g. #ZH425376)
        if (columnName === 'order_name') {
            return (lead as any).order_name || (lead as any).order_number || '—';
        }
        // EDD/Failed-Delivery: courier AWB / tracking number
        if (columnName === 'awb') {
            return (lead as any).awb || '—';
        }
        // EDD/Failed-Delivery: courier the shipment is with (Clickpost account_code).
        // For aggregators (Prozo, Shiprocket) this is the aggregator, not the last-mile
        // carrier — Clickpost never sends child_courier_partner for us.
        if (columnName === 'courier_partner') {
            return (lead as any).courier_partner || '—';
        }
        // EDD/Failed-Delivery: courier's origin facility from the PickedUp scan. Blank until
        // the parcel is collected, so 'Not picked up' rather than a bare dash — the two mean
        // different things to an agent chasing a shipment.
        if (columnName === 'pickup_hub') {
            const hub = (lead as any).pickup_hub;
            if (hub) return hub;
            return (lead as any).dispatched_date ? '—' : 'Not picked up';
        }
        // EDD/Failed-Delivery: first PickedUp event = left the warehouse
        if (columnName === 'dispatched_date') {
            const value = (lead as any).dispatched_date;
            if (value) {
                try {
                    return format(parseISO(value), 'MMM dd, yyyy');
                } catch {
                    return String(value);
                }
            }
            return 'Not dispatched';
        }
        // EDD/Failed-Delivery: days elapsed since dispatch
        if (columnName === 'dispatch_ageing') {
            const days = (lead as any).dispatch_ageing_days;
            if (days === null || days === undefined) return '—';
            return days === 0 ? 'Today' : days === 1 ? '1 day' : `${days} days`;
        }
        if (columnName === 'is_qualified') {
            // Display qualified status
            if (lead.is_qualified === 1 || lead.is_qualified === true) {
                return 'Yes';
            } else {
                return 'No';
            }
        }

        // Handle call log columns
        if (columnName === 'last_call_status') {
            const callLogs = (lead as any).callLogs;
            if (callLogs && Array.isArray(callLogs) && callLogs.length > 0) {
                const latestCall = callLogs[0]; // Already sorted by start_time DESC from backend
                return latestCall.call_status || latestCall.call_disposition || 'N/A';
            }
            return 'No calls';
        }
        if (columnName === 'last_call_duration') {
            const callLogs = (lead as any).callLogs;
            if (callLogs && Array.isArray(callLogs) && callLogs.length > 0) {
                const latestCall = callLogs[0];
                const duration = latestCall.talk_duration || latestCall.total_duration;
                if (duration !== null && duration !== undefined) {
                    // Format duration in seconds to mm:ss or hh:mm:ss
                    const totalSeconds = parseInt(duration, 10);
                    if (isNaN(totalSeconds)) return String(duration);
                    const hours = Math.floor(totalSeconds / 3600);
                    const minutes = Math.floor((totalSeconds % 3600) / 60);
                    const seconds = totalSeconds % 60;
                    if (hours > 0) {
                        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                    }
                    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                }
                return '0:00';
            }
            return 'No calls';
        }

        // Handle updated_date specifically
        if (columnName === 'updated_date') {
            const value = lead.last_updated_date || lead.updated_date;
            if (value && typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
                try {
                    return format(parseISO(value), 'MMM dd, yyyy HH:mm');
                } catch {
                    return value;
                }
            }
            return value ? String(value) : 'N/A';
        }
        
        // Handle direct properties
        if (lead[columnName] !== undefined && lead[columnName] !== null) {
            const value = lead[columnName];
            
            if (typeof value === 'string') {
                // Format dates
                if (columnName.includes('date') && value.match(/^\d{4}-\d{2}-\d{2}/)) {
                    try {
                        return format(parseISO(value), 'MMM dd, yyyy HH:mm');
                    } catch {
                        return value;
                    }
                }
                return value;
            } else if (typeof value === 'boolean') {
                return value ? 'Yes' : 'No';
            } else {
                return String(value);
            }
        }

        // Handle custom fields - Check formattedCustomInfo first (preferred format)
        if (lead.formattedCustomInfo && lead.formattedCustomInfo[columnName]) {
            return lead.formattedCustomInfo[columnName].display || String(lead.formattedCustomInfo[columnName].value);
        }

        // Fallback to customFields array (new API format)
        if (lead.customFields && Array.isArray(lead.customFields)) {
            const customField = lead.customFields.find(
                field => field.column?.col_name === columnName
            );

            if (customField) {
                // Check optionDetails first (new format), then optionDetailsbyUuid (old format)
                if (customField.optionDetails?.opt_name) {
                    return customField.optionDetails.opt_name;
                } else if (customField.optionDetailsbyUuid?.opt_name) {
                    return customField.optionDetailsbyUuid.opt_name;
                } else if (customField.value_text !== undefined && customField.value_text !== null) {
                    return String(customField.value_text);
                } else if (customField.value_int !== undefined && customField.value_int !== null) {
                    return String(customField.value_int);
                } else if (customField.value_date !== undefined && customField.value_date !== null) {
                    return String(customField.value_date);
                } else if (customField.custom_field_value !== undefined) {
                    return String(customField.custom_field_value);
                }
            }
        }

        // Legacy support: Check customInfo array (old API format)
        if (lead.customInfo && Array.isArray(lead.customInfo)) {
            const customField = lead.customInfo.find(
                field => field.column?.col_name === columnName
            );

            if (customField) {
                if (customField.optionDetailsbyUuid?.opt_name) {
                    return customField.optionDetailsbyUuid.opt_name;
                } else if (customField.custom_field_value !== undefined) {
                    return String(customField.custom_field_value);
                }
            }
        }

        return 'N/A';
    }, [columns]);

    // Render filter input based on column type
    const renderFilterInput = (column: Column) => {
        // Skip owner column as it has its own toolbar filter
        // Skip last_remark and last_call_duration — derived/joined fields, not directly filterable
        if (column.col_name === 'owner' || column.col_name === 'last_remark' || column.col_name === 'last_call_duration') {
            return null;
        }

        const filterValue = tempFilters[column.col_name] as CustomFieldFilter | undefined;
        const currentValue = filterValue?.value || [];

        // Special handling for is_qualified column
        if (column.col_name === 'is_qualified') {
            const qualifiedFilterValue = tempFilters[column.col_name] as CustomFieldFilter | undefined;
            return (
                <select
                    value={qualifiedFilterValue?.value || ''}
                    onChange={(e) => handleFilterChange(column.col_name, e.target.value, 'select')}
                    className="w-full px-2 py-1 text-sm font-normal border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brandColor focus:border-transparent"
                >
                    <option value="">All</option>
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                </select>
            );
        }

        switch (column.col_type) {
            case 'select':
                return (
                    <div className="relative">
                        <FSelect
                            options={column.options || []}
                            value={Array.isArray(currentValue) ? currentValue : []}
                            onChange={(selectedValues) => handleFilterChange(column.col_name, selectedValues, 'select')}
                            placeholder="Filter..."
                            searchable={true}
                            showCount={false}
                            className="w-full font-normal"
                            wideDropdown={true}
                            dropdownClassName="min-w-[200px]"
                        />
                    </div>
                );

            case 'date':
            case 'dateTime':
                const dateFilterValue = tempFilters[column.col_name] as CustomFieldFilter | undefined;
                const fromValue = dateFilterValue?.from || '';
                const toValue = dateFilterValue?.to || '';

                return (
                    <div className="space-y-2">
                        {/* From date input */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">From date</label>
                            <input
                                type="date"
                                value={fromValue}
                                onChange={(e) => {
                                    handleFilterChange(column.col_name, {
                                        from: e.target.value,
                                        to: toValue
                                    }, column.col_type as 'date' | 'dateTime');
                                }}
                                className="w-full px-2 py-1.5 text-sm font-normal border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brandColor focus:border-transparent"
                            />
                        </div>

                        {/* To date input */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">To date</label>
                            <input
                                type="date"
                                value={toValue}
                                onChange={(e) => {
                                    handleFilterChange(column.col_name, {
                                        from: fromValue,
                                        to: e.target.value
                                    }, column.col_type as 'date' | 'dateTime');
                                }}
                                className="w-full px-2 py-1.5 text-sm font-normal border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brandColor focus:border-transparent"
                                min={fromValue || undefined}
                            />
                        </div>
                    </div>
                );

            case 'numberRange': {
                // Day-count columns. EDD Ageing is signed (negative = already overdue),
                // so negative input is allowed and no min attribute is set.
                const rangeFilter = tempFilters[column.col_name] as CustomFieldFilter | undefined;
                const minValue = rangeFilter?.min ?? '';
                const maxValue = rangeFilter?.max ?? '';

                return (
                    <div className="flex items-center gap-1.5">
                        <input
                            type="number"
                            value={minValue}
                            onChange={(e) => handleFilterChange(column.col_name, { min: e.target.value }, 'numberRange')}
                            placeholder="Min"
                            className="w-full min-w-0 px-2 py-1 text-sm font-normal border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brandColor focus:border-transparent"
                        />
                        <span className="text-xs text-gray-400 shrink-0">to</span>
                        <input
                            type="number"
                            value={maxValue}
                            onChange={(e) => handleFilterChange(column.col_name, { max: e.target.value }, 'numberRange')}
                            placeholder="Max"
                            className="w-full min-w-0 px-2 py-1 text-sm font-normal border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brandColor focus:border-transparent"
                        />
                    </div>
                );
            }

            default:
                return (
                    <input
                        type="text"
                        value={currentValue}
                        onChange={(e) => handleFilterChange(column.col_name, e.target.value, 'text')}
                        placeholder={`Filter...`}
                        className="w-full px-2 py-1 text-sm font-normal border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brandColor focus:border-transparent"
                    />
                );
        }
    };

    // Get visible columns for rendering - include UTM/FB/LI columns that exist in data
    const displayColumns = useMemo(() => {
        // Columns pushed to the end, in order — per screen, from bucketMode.ts.
        const isReorderPage = mode.isReorder;
        const isEddPage = mode.isEdd;
        const isFailedDeliveryPage = mode.isFailedDelivery;
        const dateColumnsAtEnd = mode.dateColumnsAtEnd;

        // Tracking columns (UTM, FB, LI) should come after regular columns but before date columns
        const trackingColumns = [
            'utm_source', 'utm_campaign', 'utm_medium', 'utm_content', 'utm_term',
            'utm_location', 'utm_device', 'utm_network', 'utm_platform',
            'fb_platform', 'fb_ad_name', 'fb_campaign_name', 'li_campaign_name'
        ];

        // Get all columns from API
        const apiColumns = columns.filter(col => visibleColumns.has(col.col_name));

        // Add additional columns that aren't in API columns but are in the data
        // col_order ensures proper positioning: owner at 100, call logs at 101-102
        const additionalDataColumns: Column[] = [
            { col_name: 'disposition_l1', col_label: 'Lead Status 1', col_type: 'select', is_custom: false, col_order: 3, options: dispositionL1Options },
            { col_name: 'disposition_l2', col_label: 'Lead Status 2', col_type: 'select', is_custom: false, col_order: 4, options: dispositionL2Options },
            { col_name: 'disposition_l3', col_label: 'Lead Status 3', col_type: 'select', is_custom: false, col_order: 5, options: dispositionL3Options },
            { col_name: 'owner', col_label: 'Owner', col_type: 'text', is_custom: false, col_order: 100 },
            { col_name: 'last_call_status', col_label: 'Last Call Status', col_type: 'text', is_custom: false, col_order: 101 },
            { col_name: 'last_call_duration', col_label: 'Last Call Duration', col_type: 'text', is_custom: false, col_order: 102 },
            { col_name: 'last_remark', col_label: 'Last Remark', col_type: 'text', is_custom: false },
            { col_name: 'product_info', col_label: 'Product Information', col_type: 'text', is_custom: false, col_order: 7 },
            { col_name: 'address', col_label: 'Address', col_type: 'text', is_custom: false, col_order: 8 },
            { col_name: 'is_qualified', col_label: 'Qualified', col_type: 'boolean', is_custom: false },
            { col_name: 'customer_type', col_label: 'Customer Type', col_type: 'text', is_custom: false },
            // Reorder-specific columns
            { col_name: 'reorder_due_date', col_label: 'Due Date', col_type: 'date', is_custom: false, col_order: 5 },
            { col_name: 'status_text', col_label: 'Due Status', col_type: 'text', is_custom: false, col_order: 6 },
            { col_name: 'last_abc_date', col_label: 'Last ABC Date', col_type: 'date', is_custom: false },
            // RTO-specific columns (after call duration at 102): Payment Type > Order Date > RTO Date > RTO Status
            { col_name: 'payment_gateway', col_label: (isEddPage || isFailedDeliveryPage || mode.isCodConfirm) ? 'Payment Mode' : 'Payment Type', col_type: 'select', is_custom: false, col_order: 103, options: [
                { opt_id: 'pending' as any, opt_name: 'Cash on Delivery' },
                { opt_id: 'paid' as any, opt_name: 'Prepaid' },
                { opt_id: 'partially_paid' as any, opt_name: 'Partial Payment' },
                { opt_id: 'refunded' as any, opt_name: 'Refunded' },
            ] },
            { col_name: 'order_date', col_label: 'Order Date', col_type: 'date', is_custom: false, col_order: 104 },
            { col_name: 'rto_date', col_label: 'RTO Date', col_type: 'date', is_custom: false, col_order: 105 },
            { col_name: 'rto_status', col_label: 'RTO Status', col_type: 'select', is_custom: false, col_order: 106, options: [
                { opt_id: 11, opt_name: 'RTO Requested' },
                { opt_id: 12, opt_name: 'RTO Marked' },
                { opt_id: 21, opt_name: 'RTO In Transit' },
                { opt_id: 14, opt_name: 'RTO Delivered' },
            ] },
            // EDD-specific columns: Order Date > Expected Delivery Date > Priority > Current Status
            { col_name: 'expected_delivery_date', col_label: 'Expected Delivery Date', col_type: 'date', is_custom: false, col_order: 105 },
            // No 'Failed Delivery' option: a failed attempt moves the lead to the NDR tab,
            // so on this screen priority is only ever a breach state against the EDD.
            { col_name: 'edd_priority', col_label: 'Priority', col_type: 'select', is_custom: false, col_order: 106, options: [
                { opt_id: 'Breached' as any, opt_name: 'Breached' },
                { opt_id: 'Due Today' as any, opt_name: 'Due Today' },
                { opt_id: 'Due Soon' as any, opt_name: 'Due Soon' },
                { opt_id: 'In Transit' as any, opt_name: 'In Transit' },
            ] },
            // Confirm Orders columns. Couriers collect around 6 PM: an order placed before
            // ~2 PM ships that evening, a later one the next evening.
            { col_name: 'ships_by', col_label: 'Ships By', col_type: 'select', is_custom: false, col_order: 106.1, options: [
                { opt_id: 'Pickup due now' as any, opt_name: 'Pickup due now' },
                { opt_id: 'Today ~6 PM' as any, opt_name: 'Today ~6 PM' },
                { opt_id: 'Tomorrow ~6 PM' as any, opt_name: 'Tomorrow ~6 PM' },
            ] },
            { col_name: 'cod_tier', col_label: 'Why Call', col_type: 'select', is_custom: false, col_order: 106.2, options: [
                { opt_id: 'Returned before' as any, opt_name: 'Returned before' },
                { opt_id: 'First order (worked by Sales)' as any, opt_name: 'First order (worked by Sales)' },
            ] },
            { col_name: 'customer_history', col_label: 'Customer History', col_type: 'text', is_custom: false, col_order: 106.3 },
            { col_name: 'total_price', col_label: 'Order Value', col_type: 'numberRange', is_custom: false, col_order: 106.4 },
            { col_name: 'current_status', col_label: 'Current Status', col_type: 'select', is_custom: false, col_order: 107,
              options: shipmentFilterOptions.current_status.map(v => ({ opt_id: v as any, opt_name: v })) },
            // GoKwik's COD risk score, tagged on the Shopify order at checkout. Shared by
            // EDD/NDR (via shipmentFilterOptions.risk) and RTO (fixed list -- no filter-options
            // endpoint on that screen, and the taxonomy never changes).
            { col_name: 'risk', col_label: 'Risk', col_type: 'select', is_custom: false, col_order: 107.5,
              options: (isEddPage || isFailedDeliveryPage)
                ? shipmentFilterOptions.risk.map(v => ({ opt_id: v as any, opt_name: v }))
                : [
                    { opt_id: 'High Risk' as any, opt_name: 'High Risk' },
                    { opt_id: 'Medium Risk' as any, opt_name: 'Medium Risk' },
                    { opt_id: 'Low Risk' as any, opt_name: 'Low Risk' },
                  ] },
            // How complete is this customer's health/demographic profile
            // (crm_customer_profiles, keyed by phone -- shared across all of a customer's
            // leads). Available on every lead screen, not just EDD/NDR/RTO.
            // Complete = all 7 fields filled, Missing = none filled, Incomplete = between.
            { col_name: 'profile_captured', col_label: 'Profile Captured', col_type: 'select', is_custom: false, col_order: 20,
              options: [
                { opt_id: 'Complete' as any, opt_name: 'Complete' },
                { opt_id: 'Incomplete' as any, opt_name: 'Incomplete' },
                { opt_id: 'Missing' as any, opt_name: 'Missing' },
              ] },
            { col_name: 'days_late', col_label: 'Days Late', col_type: 'numberRange', is_custom: false, col_order: 108 },
            // Signed distance to the courier-promised EDD: counts down before it ("in 5d"),
            // then up after it ("3d late"). Superset of days_late, which is 0 until breach.
            // numberRange accepts negatives, so "-7 to -1" reads as "overdue by up to a week".
            { col_name: 'edd_ageing', col_label: 'EDD Ageing', col_type: 'numberRange', is_custom: false, col_order: 108.5 },
            { col_name: 'ndr_reason', col_label: 'Failure Reason', col_type: 'text', is_custom: false, col_order: 109 },
            // EDD / Failed-Delivery shipment columns. Sourced from clickpost_webhook_logs
            // (waybill + courier account_code + first PickedUp event) — see
            // _fetch_shipment_info in shopify.py.
            { col_name: 'order_name', col_label: 'Order ID', col_type: 'text', is_custom: false, col_order: 110 },
            // Agent-placed vs customer-placed. The filter offers the three raw values the
            // backend CASE emits — not the display labels, which carry the agent's name and
            // would need one option per agent (60+) to be selectable.
            { col_name: 'order_source', col_label: 'Order Source', col_type: 'select', is_custom: false, col_order: 110.5, options: [
                { opt_id: 'agent' as any, opt_name: 'Agent' },
                { opt_id: 'agent_untagged' as any, opt_name: 'Agent (untagged)' },
                { opt_id: 'direct' as any, opt_name: 'Direct' },
            ] },
            { col_name: 'awb', col_label: 'AWB', col_type: 'text', is_custom: false, col_order: 111 },
            { col_name: 'courier_partner', col_label: 'Courier Partner', col_type: 'select', is_custom: false, col_order: 112,
              options: shipmentFilterOptions.courier_partner.map(v => ({ opt_id: v as any, opt_name: v })) },
            // The courier's origin facility from the PickedUp scan — not a ZeroHarm warehouse,
            // and blank until the parcel is actually collected.
            { col_name: 'pickup_hub', col_label: 'Pickup Hub', col_type: 'select', is_custom: false, col_order: 112.5,
              options: shipmentFilterOptions.pickup_hub.map(v => ({ opt_id: v as any, opt_name: v })) },
            { col_name: 'dispatched_date', col_label: 'Dispatched Date', col_type: 'date', is_custom: false, col_order: 113 },
            { col_name: 'dispatch_ageing', col_label: 'Dispatch Ageing', col_type: 'numberRange', is_custom: false, col_order: 114 },
            // Failed Delivery: one column per delivery attempt, oldest of the last three first
            { col_name: 'ndr_1', col_label: 'NDR 1', col_type: 'text', is_custom: false, col_order: 120 },
            { col_name: 'ndr_2', col_label: 'NDR 2', col_type: 'text', is_custom: false, col_order: 121 },
            { col_name: 'ndr_3', col_label: 'NDR 3', col_type: 'text', is_custom: false, col_order: 122 },
            { col_name: 'shopify_customer_id', col_label: 'Shopify Customer ID', col_type: 'text', is_custom: false },
            { col_name: 'utm_source', col_label: 'UTM Source', col_type: 'text', is_custom: false },
            { col_name: 'utm_campaign', col_label: 'UTM Campaign', col_type: 'text', is_custom: false },
            { col_name: 'utm_medium', col_label: 'UTM Medium', col_type: 'text', is_custom: false },
            { col_name: 'utm_content', col_label: 'UTM Content', col_type: 'text', is_custom: false },
            { col_name: 'utm_term', col_label: 'UTM Term', col_type: 'text', is_custom: false },
            { col_name: 'utm_location', col_label: 'UTM Location', col_type: 'text', is_custom: false },
            { col_name: 'utm_device', col_label: 'UTM Device', col_type: 'text', is_custom: false },
            { col_name: 'utm_network', col_label: 'UTM Network', col_type: 'text', is_custom: false },
            { col_name: 'utm_platform', col_label: 'UTM Platform', col_type: 'text', is_custom: false },
            { col_name: 'fb_platform', col_label: 'FB Platform', col_type: 'text', is_custom: false },
            { col_name: 'fb_ad_name', col_label: 'FB Ad Name', col_type: 'text', is_custom: false },
            { col_name: 'fb_campaign_name', col_label: 'FB Campaign', col_type: 'text', is_custom: false },
            { col_name: 'li_campaign_name', col_label: 'LI Campaign', col_type: 'text', is_custom: false },
        ].filter(col => visibleColumns.has(col.col_name) && !apiColumns.find(c => c.col_name === col.col_name));

        // Combine all columns
        const allColumns = [...apiColumns, ...additionalDataColumns];

        // Sort columns:
        // 1. Phone always first
        // 2. Regular columns sorted by col_order
        // 3. Tracking columns (UTM, FB, LI)
        // 4. Date columns (created_date, updated_date) ALWAYS at end
        // For reorder pages, define specific column order: phone, name, email, order_date
        const reorderColumnOrder = ['phone', 'name', 'email'];

        // Absolute left-to-right order for the EDD screen. Deliberately ignores col_order:
        // col_order comes from the DB and is shared by every screen, so it can't express a
        // per-screen arrangement. Anything not listed here (label, last_abc_date, or a column
        // the user switches on by hand) trails after the last column listed.
        //
        // The shipment identifiers — Order ID, Courier Partner, AWB — sit immediately after
        // Phone, so an agent on a call can quote them without scrolling. The chronology
        // block (ordered -> dispatched -> ageing -> due -> where it is now) follows.
        const EDD_COLUMN_ORDER = [
            'phone', 'order_name', 'order_source', 'courier_partner', 'awb',
            'name',
            'disposition_l1', 'disposition_l2', 'disposition_l3',
            'owner', 'edd_priority',
            'product_info', 'payment_gateway',
            'pickup_hub', 'order_date',
            'dispatched_date', 'dispatch_ageing',
            'expected_delivery_date', 'edd_ageing',
            'current_status', 'risk', 'profile_captured',
        ];

        // NDR screen: identical spine to EDD, with the two EDD-only columns swapped out —
        // Days Late in place of EDD Ageing, and the three attempt columns after Current
        // Status. No Priority column: on this screen it is always "Failed Delivery".
        const NDR_COLUMN_ORDER = [
            'phone', 'order_name', 'order_source', 'courier_partner', 'awb',
            'name',
            'disposition_l1', 'disposition_l2', 'disposition_l3',
            'owner',
            'product_info', 'payment_gateway',
            'pickup_hub', 'order_date',
            'dispatched_date', 'dispatch_ageing',
            'expected_delivery_date', 'days_late',
            'current_status', 'risk', 'profile_captured',
            'ndr_1', 'ndr_2', 'ndr_3',
        ];

        // Confirm Orders: the deadline and the reason first, then what the agent quotes
        // on the call (order, value, products), then who placed it.
        const COD_COLUMN_ORDER = [
            'phone', 'order_name', 'ships_by', 'cod_tier', 'risk',
            'name',
            'disposition_l1', 'disposition_l2', 'disposition_l3',
            'owner', 'customer_history', 'total_price', 'product_info',
            'payment_gateway', 'order_source', 'order_date',
        ];

        const explicitOrder = isEddPage
            ? EDD_COLUMN_ORDER
            : isFailedDeliveryPage
            ? NDR_COLUMN_ORDER
            : mode.isCodConfirm
            ? COD_COLUMN_ORDER
            : null;

        const sortedColumns = allColumns.sort((a, b) => {
            // An explicit per-screen order outranks every other rule below, including
            // col_order and the phone special-case (phone is simply index 0 in the list).
            if (explicitOrder) {
                const aIdx = explicitOrder.indexOf(a.col_name);
                const bIdx = explicitOrder.indexOf(b.col_name);
                if (aIdx !== -1 || bIdx !== -1) {
                    if (aIdx === -1) return 1;   // unlisted columns trail listed ones
                    if (bIdx === -1) return -1;
                    return aIdx - bIdx;
                }
            }

            // Phone always comes first
            if (a.col_name === 'phone') return -1;
            if (b.col_name === 'phone') return 1;

            // For reorder pages, enforce specific order for key columns
            if (isReorderPage) {
                const aReorderIndex = reorderColumnOrder.indexOf(a.col_name);
                const bReorderIndex = reorderColumnOrder.indexOf(b.col_name);
                if (aReorderIndex !== -1 && bReorderIndex !== -1) {
                    return aReorderIndex - bReorderIndex;
                }
                if (aReorderIndex !== -1) return -1;
                if (bReorderIndex !== -1) return 1;
            }

            // Date columns always at the end
            const aIsDateCol = dateColumnsAtEnd.includes(a.col_name);
            const bIsDateCol = dateColumnsAtEnd.includes(b.col_name);
            if (aIsDateCol && !bIsDateCol) return 1;
            if (!aIsDateCol && bIsDateCol) return -1;
            if (aIsDateCol && bIsDateCol) {
                // Sort date columns in specific order
                return dateColumnsAtEnd.indexOf(a.col_name) - dateColumnsAtEnd.indexOf(b.col_name);
            }

            // Tracking columns come after regular columns
            const aIsTracking = trackingColumns.includes(a.col_name);
            const bIsTracking = trackingColumns.includes(b.col_name);
            if (aIsTracking && !bIsTracking) return 1;
            if (!aIsTracking && bIsTracking) return -1;
            if (aIsTracking && bIsTracking) {
                // Sort tracking columns in their defined order
                return trackingColumns.indexOf(a.col_name) - trackingColumns.indexOf(b.col_name);
            }

            // For regular columns, sort by col_order
            const aOrder = a.col_order ?? 999;
            const bOrder = b.col_order ?? 999;
            if (aOrder !== bOrder) {
                return aOrder - bOrder;
            }

            // Fallback to alphabetical by label
            return (a.col_label || '').localeCompare(b.col_label || '');
        });

        // Filter out hidden columns
        return sortedColumns.filter(col => !hiddenColumns.includes(col.col_name));
    }, [columns, visibleColumns, hiddenColumns, dispositionL1Options, dispositionL2Options, dispositionL3Options, shipmentFilterOptions]);

    // Toggle column visibility and save to database
    const toggleColumnVisibility = async (columnName: string) => {
        // Prevent toggling phone column - it is always required and visible
        if (columnName === 'phone') {
            return;
        }

        const newVisible = new Set(visibleColumns);
        if (newVisible.has(columnName)) {
            newVisible.delete(columnName);
        } else {
            newVisible.add(columnName);
        }
        setVisibleColumns(newVisible);

        // Save to both localStorage and database
        saveColumnPreferencesToStorage(newVisible);
        await saveColumnSettings(newVisible);
    };
    
    // Save column settings to database
    const saveColumnSettings = async (visibleCols: Set<string>) => {
        try {
            // Get column UUIDs for selected columns
            const selectedColumnUuids = columns
                .filter(col => visibleCols.has(col.col_name) && col.uuid)
                .map(col => col.uuid!)
                .filter(uuid => uuid !== undefined);
                
            const response = await optimizedApi.post('/user-leads-settings/columns/save', {
                selected_columns: selectedColumnUuids
            });
            
            if (response.success) {
            } else {
                console.error('❌ Failed to save column settings:', response.message);
            }
        } catch (error) {
            console.error('❌ Error saving column settings:', error);
        }
    };


    // Handle bulk change owner
    const handleChangeOwner = () => {
        setIsChangeOwnerModalOpen(true);
        setIsBulkActionsOpen(false);
    };

    // Handle bulk add reminder
    const handleBulkAddReminder = useCallback(() => {
        setIsBulkReminderModalOpen(true);
        setIsBulkActionsOpen(false);
    }, []);

    // Optimized form handlers to prevent input lag
    const handleReminderTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setReminderTitle(e.target.value);
    }, []);

    const handleReminderDateTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setReminderDateTime(e.target.value);
    }, []);

    const handleReminderAlertChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setReminderAlertBefore(e.target.value);
    }, []);

    const handleOwnerChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedNewOwner(e.target.value);
    }, []);

    const handleOwnerRemarkChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setOwnerChangeRemark(e.target.value);
    }, []);

    const handleCloseReminderModal = useCallback(() => {
        setIsBulkReminderModalOpen(false);
        setReminderTitle('');
        setReminderDateTime('');
        setReminderAlertBefore('15 min');
    }, []);

    const handleCloseOwnerModal = useCallback(() => {
        setIsChangeOwnerModalOpen(false);
        setSelectedNewOwner('');
        setOwnerChangeRemark('');
    }, []);

    const handleCloseDeleteModal = useCallback(() => {
        setIsDeleteConfirmModalOpen(false);
    }, []);

    /**
     * Selected rows → the lead uuids the bulk endpoints expect.
     *
     * Ownership and reminders are per customer, but a row is not always a
     * customer: reorder rows are per due line item, and the shipment screens
     * become per order once the API sends order_uuid. Both collapse to the
     * lead here, deduplicated, so selecting two rows for one customer assigns
     * them once. On a screen where the row IS the lead this resolves to the
     * same uuid it started with.
     */
    const resolveSelectedLeadUuids = useCallback((selectedRowIds: string[]): string[] => {
        const leadUuids = new Set<string>();
        for (const rowId of selectedRowIds) {
            const lead = leads.find(l => getRowId(mode, l as any) === rowId);
            const leadUuid = (lead as any)?.lead_uuid || lead?.uuid;
            if (leadUuid) leadUuids.add(leadUuid);
        }
        return Array.from(leadUuids);
    }, [leads, mode]);

    // On the per-order In-Transit screens a row is one order's work item.
    const isWorkItemScreen = mode.isRto || mode.isEdd || mode.isFailedDelivery || mode.isCodConfirm;
    const resolveSelectedIssueUuids = useCallback((selectedRowIds: string[]): string[] => {
        const ids = new Set<string>();
        for (const rowId of selectedRowIds) {
            const row = leads.find(l => getRowId(mode, l as any) === rowId) as any;
            if (row?.issue_uuid) ids.add(row.issue_uuid);
        }
        return Array.from(ids);
    }, [leads, mode]);

    // Process bulk owner change - single API call for all leads
    const processBulkOwnerChange = async () => {
        if (!selectedNewOwner || !ownerChangeRemark.trim() || selectedRows.size === 0) {
            showToast('Please select a new owner and add a remark', 'warning');
            return;
        }

        setIsProcessingBulkAction(true);
        try {
            const selectedRowIds = Array.from(selectedRows);

            const selectedLeadUuids = resolveSelectedLeadUuids(selectedRowIds);

            // RTO / EDD / Failed Delivery: assign the selected ORDERS' work. Moving the
            // customer here would change their Sales/Retention owner.
            if (isWorkItemScreen) {
                const res = await apiRequest('/work-items/bulk-assign', {
                    method: 'POST',
                    body: JSON.stringify({
                        issue_uuids: resolveSelectedIssueUuids(selectedRowIds),
                        owner_uuid: selectedNewOwner,
                    }),
                });
                showToast(res.message || (res.success ? 'Orders assigned' : 'Failed to assign orders'),
                          res.success ? 'success' : 'error');
                if (res.success) {
                    await fetchLeads();
                    setSelectedRows(new Set());
                    setSelectAll(false);
                }
                return;
            }

            // Single bulk API call instead of looping
            const response = await apiRequest('/leads/bulk-assign', {
                method: 'POST',
                body: JSON.stringify({
                    lead_uuids: selectedLeadUuids,
                    owner_uuid: selectedNewOwner,
                    remarks: ownerChangeRemark.trim()
                })
            });

            if (response.success) {
                // apiRequest puts the server's body under .data (it has no `data` key of
                // its own), so the counts live there, not on the wrapper.
                const r = (response.data ?? {}) as {
                    assigned?: number;
                    skipped_detail?: Array<{ lead_uuid: string; name: string | null; phone: string | null; worked_by: string | null; worked_at: string; outcome?: string | null }>;
                    skipped_ordered_detail?: Array<{ lead_uuid: string; name: string | null; phone: string | null; order_number: string | null; ordered_at: string | null }>;
                };
                const successCount = r.assigned ?? selectedLeadUuids.length;
                // Held back server-side so they aren't handed out unnoticed — ask instead of
                // hiding it: leads another agent worked in the last 48h, and abandoned-cart
                // customers who have already ordered.
                const held = r.skipped_detail ?? [];
                const ordered = r.skipped_ordered_detail ?? [];
                if (successCount > 0) showToast(`Assigned ${successCount} lead(s).`, 'success');
                if (held.length > 0 || ordered.length > 0) {
                    setHeldBack({ leads: held, ordered, ownerUuid: selectedNewOwner, remark: ownerChangeRemark.trim() });
                }
                // Refresh the table data
                await fetchLeads();
                // Clear selection
                setSelectedRows(new Set());
                setSelectAll(false);
            } else {
                showToast(response.message || 'Failed to assign leads. Please try again.', 'error');
            }
        } catch (error) {
            console.error('❌ Bulk owner change error:', error);
            showToast('An error occurred. Please try again.', 'error');
        } finally {
            setIsProcessingBulkAction(false);
            setIsChangeOwnerModalOpen(false);
            setSelectedNewOwner('');
            setOwnerChangeRemark('');
        }
    };

    // Process bulk add reminder
    const processBulkReminder = async () => {
        if (!reminderTitle.trim() || !reminderDateTime || selectedRows.size === 0) {
            showToast('Please fill in all required fields', 'warning');
            return;
        }

        setIsProcessingBulkAction(true);
        try {
            const selectedRowIds = Array.from(selectedRows);

            const selectedLeadUuids = resolveSelectedLeadUuids(selectedRowIds);

            const response = await optimizedApi.post('/leads/reminder_lead', {
                leadUuid: selectedLeadUuids,
                event_title: reminderTitle,
                daterange: reminderDateTime,
                alert_before: reminderAlertBefore
            });

            if (response.success) {
                showToast(`Successfully added reminders to ${selectedLeadUuids.length} lead(s)`, 'success');
                // Clear selection
                setSelectedRows(new Set());
                setSelectAll(false);
            } else {
                showToast(`Failed to add reminders: ${response.message || 'Unknown error'}`, 'error');
            }
        } catch (error) {
            console.error('❌ Bulk reminder error:', error);
            showToast('An error occurred. Please try again.', 'error');
        } finally {
            setIsProcessingBulkAction(false);
            setIsBulkReminderModalOpen(false);
            setReminderTitle('');
            setReminderDateTime('');
            setReminderAlertBefore('15 min');
        }
    };

    // Process bulk delete
    const processBulkDelete = async () => {
        if (selectedRows.size === 0) return;

        setIsProcessingBulkAction(true);
        try {
            const selectedRowIds = Array.from(selectedRows);

            const selectedLeadUuids = resolveSelectedLeadUuids(selectedRowIds);

            const response = await optimizedApi.post('/leads/delete_lead', {
                leadUuids: selectedLeadUuids
            });

            if (response.success) {
                showToast(`Successfully deleted ${selectedLeadUuids.length} lead(s)`, 'success');
                // Refresh the table data
                await fetchLeads();
                // Clear selection
                setSelectedRows(new Set());
                setSelectAll(false);
            } else {
                showToast(`Failed to delete leads: ${response.message || 'Unknown error'}`, 'error');
            }
        } catch (error) {
            console.error('❌ Bulk delete error:', error);
            showToast('An error occurred. Please try again.', 'error');
        } finally {
            setIsProcessingBulkAction(false);
            setIsDeleteConfirmModalOpen(false);
        }
    };

    // Show loading state
    if (isLoading && leads.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading leads...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col">
            {/* Compact Single Line Toolbar */}
            <div className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0 sticky top-0 z-40">
                <div className="px-6 py-3">
                    <div className="flex items-center justify-between">
                        {/* Left Section - Title and Stats */}
                        <div className="flex items-center gap-6">
                            <div className="flex items-center space-x-3">
                                <div className="p-2 bg-brandColor rounded-lg text-white">
                                    {pageIcon || <Calendar className="w-6 h-6" />}
                                </div>
                                <div>
                                    <h1 className="text-lg font-semibold text-gray-900">{pageTitle || (agentCreatedOnly ? 'Agent Orders' : leadBucketFilter === 'phone' ? 'Phone Leads' : 'Abandoned Orders')}</h1>
                                    <p className="text-xs text-gray-500">
                                        {dateRange.from && dateRange.to ? (
                                            <>
                                                Showing {pagination.totalItems.toLocaleString()} leads from{' '}
                                                <span className="font-medium text-gray-700">
                                                    {format(parseISO(dateRange.from), 'MMM dd, yyyy')}
                                                </span>
                                                {' '}to{' '}
                                                <span className="font-medium text-gray-700">
                                                    {format(parseISO(dateRange.to), 'MMM dd, yyyy')}
                                                </span>
                                                {dateRange.isDefault && (
                                                    <span className="ml-2 text-xs text-blue-600">(Last 14 days: new leads and new carts)</span>
                                                )}
                                            </>
                                        ) : (
                                            `Showing ${pagination.totalItems.toLocaleString()} leads`
                                        )}
                                    </p>
                                </div>
                            </div>
                            {/* Bulk Actions Dropdown - Show only when items are selected */}
                            {selectedRows.size > 0 && (
                                <div className="relative pl-4 border-l border-gray-400" data-tour="bulk-actions-hint">
                                    <button
                                        onClick={() => setIsBulkActionsOpen(!isBulkActionsOpen)}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-brandColor text-white text-sm font-medium rounded-md hover:bg-brandColor transition-colors"
                                    >
                                        <span>Bulk Actions ({selectedRows.size})</span>
                                        <ChevronDown className={`h-4 w-4 transition-transform ${isBulkActionsOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {/* Dropdown Menu */}
                                    {isBulkActionsOpen && (
                                        <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                                            <div className="py-1">
                                                <button
                                                    onClick={handleChangeOwner}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                                                    <UserCheck className="h-4 w-4 text-brandColor" />
                                                    Change Owner
                                                </button>
                                                {canExport && (
                                                    <button
                                                        onClick={() => setIsExportModalOpen(true)}
                                                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                                                        <Download className="h-4 w-4 text-gray-600" />
                                                        Export Selected
                                                    </button>
                                                )}
                                                <button
                                                    onClick={handleBulkAddReminder}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                                                    <Bell className="h-4 w-4 text-amber-600" />
                                                    Add Reminder
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Right Section - Action Buttons */}
                        <div className="flex items-center gap-3">
                            {/* 1. Owner Filter - Hidden for Sales Reps */}
                            {!isSalesRep && (
                                <div className="flex items-center gap-2" data-tour="owner-filter">
                                    <Users className="h-4 w-4 text-gray-500" />
                                    <FSelect
                                        options={[
                                            { uuid: 'unassigned', opt_id: 'unassigned', opt_name: 'Unassigned' },
                                            ...(ownerFilterUsers || (ownerOptions.length ? ownerOptions : users)).map(user => ({
                                                uuid: user.uuid,
                                                opt_id: user.uuid,
                                                opt_name: user.user_active === false ? `${user.userName} (inactive)` : user.userName
                                            }))
                                        ]}
                                        value={Array.isArray((tempFilters['owner'] as CustomFieldFilter | undefined)?.value) ? (tempFilters['owner'] as CustomFieldFilter).value : []}
                                        onChange={(selectedValues) => handleFilterChange('owner', selectedValues, 'select')}
                                        placeholder="All Owners"
                                        searchable={true}
                                        showCount={false}
                                        className="min-w-[200px]"
                                        wideDropdown={true}
                                        dropdownClassName="min-w-[250px]"
                                    />
                                </div>
                            )}

                            {/* 2. Apply Button */}
                            <button
                                data-tour="apply-filters"
                                onClick={applyFilters}
                                disabled={!hasFiltersToApply}
                                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-all ${
                                    hasFiltersToApply
                                        ? 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                        : 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                                }`}
                                title="Apply Filters"
                            >
                                <Check className="h-4 w-4" />
                                <span className="hidden md:inline">Apply</span>
                                {Object.keys(tempFilters).length > 0 && (
                                    <span className="px-2 py-0.5 text-xs bg-brandColor text-white rounded-full font-bold">
                                        {Object.keys(tempFilters).length}
                                    </span>
                                )}
                            </button>

                            {Object.keys(activeFilters).length > 0 && (
                                <button
                                    onClick={clearFilters}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                                    title="Clear all filters"
                                >
                                    <X className="h-4 w-4" />
                                    <span className="hidden md:inline">Clear</span>
                                </button>
                            )}

                            <div className="h-6 w-px bg-gray-300"></div>

                            {/* 3. Search Component */}
                            <div data-tour="search">
                                <PipelineSearch
                                    onSearch={handleSearch}
                                    isLoading={isLoading}
                                    placeholder="Search leads by name, email, or phone..."
                                    currentSearchTerm={searchTerm}
                                    className="flex-shrink-0"
                                />
                            </div>

                            <div className="h-6 w-px bg-gray-300"></div>

                            {/* 4. Advanced Filters */}
                            <button
                                data-tour="advanced-filters"
                                onClick={() => setIsAdvancedFiltersOpen(true)}
                                className="inline-flex items-center justify-center p-2 bg-white text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all"
                                title="Advanced Filters"
                            >
                                <Filter className="h-4 w-4" />
                            </button>

                            {/* 5. Add Lead - Only show for Agent Orders page */}
                            {agentCreatedOnly && (
                                <button
                                    data-tour="add-lead"
                                    onClick={() => setIsAddLeadModalOpen(true)}
                                    className="inline-flex items-center justify-center p-2 bg-brandColor text-white rounded-lg hover:bg-brandColor transition-all hover:shadow-md"
                                    title="Add Lead"
                                >
                                    <Plus className="h-5 w-5" />
                                </button>
                            )}

                            {/* 6. Export - Hidden for Sales Rep role */}
                            {canExport && (
                                <button
                                    data-tour="export"
                                    onClick={() => setIsExportModalOpen(true)}
                                    className="inline-flex items-center justify-center p-2 bg-white text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all"
                                    title="Export Leads"
                                >
                                    <Download className="h-5 w-5" />
                                </button>
                            )}

                            {/* Column Settings */}
                            {showColumnSettings && (
                                <div className="flex items-center" data-tour="column-settings">
                                    <ColumnVisibilityDropdown
                                        columns={columns}
                                        visibleColumns={visibleColumns}
                                        onToggleColumn={toggleColumnVisibility}
                                        iconOnly={true}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Active Filters Row - Compact */}
                    {(Object.keys(activeFilters).length > 0 || searchTerm) && (
                        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-200">
                            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Active Filters:</span>
                            <div className="flex flex-wrap gap-2">
                                {/* Search Filter */}
                                {searchTerm && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs bg-green-50 text-gray-700 rounded-full border border-blue-200">
                                        <span className="font-medium">Search:</span>
                                        <span className="text-brandColor" title={searchTerm}>
                                            {searchTerm.length > 20 ? searchTerm.substring(0, 20) + '...' : searchTerm}
                                        </span>
                                        <button
                                            onClick={() => handleSearch('')}
                                            className="ml-1 text-gray-400 hover:text-red-600 transition-colors"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                )}
                                {Object.entries(activeFilters).map(([key, filterItem]) => {
                                    const filter = filterItem as CustomFieldFilter;
                                    if (!filter) return null;

                                    // Skip empty filters
                                    const hasMin = filter.min !== undefined && filter.min !== '';
                                    const hasMax = filter.max !== undefined && filter.max !== '';
                                    if (filter.type === 'date' || filter.type === 'dateTime') {
                                        // For date filters, check if from or to exists
                                        if (!filter.from && !filter.to) return null;
                                    } else if (filter.type === 'numberRange') {
                                        if (!hasMin && !hasMax) return null;
                                    } else {
                                        // For other filters, check if value exists
                                        if (!filter.value) return null;
                                    }

                                    // Get display label and value
                                    let displayLabel = key === 'owner' ? 'Owner' : columns.find(col => col.col_name === key)?.col_label || key;
                                    let displayValue = '';
                                    let titleValue = '';

                                    // Handle date filters specially
                                    if (filter.type === 'date' || filter.type === 'dateTime') {
                                        const parts = [];
                                        if (filter.from) parts.push(`From ${filter.from}`);
                                        if (filter.to) parts.push(`To ${filter.to}`);
                                        displayValue = parts.join(' ');
                                        titleValue = displayValue;
                                    }
                                    // Handle numeric range filters
                                    else if (filter.type === 'numberRange') {
                                        displayValue = hasMin && hasMax
                                            ? `${filter.min} to ${filter.max}`
                                            : hasMin ? `≥ ${filter.min}` : `≤ ${filter.max}`;
                                        titleValue = displayValue;
                                    }
                                    // Handle owner filter
                                    else if (key === 'owner' && Array.isArray(filter.value)) {
                                        const selectedOwnerNames = filter.value.map((uuid: any) => {
                                            if (uuid === 'unassigned') return 'Unassigned';
                                            const user = users.find(u => u.uuid === uuid) || ownerOptions.find(u => u.uuid === uuid);
                                            return user ? user.userName : uuid;
                                        });
                                        displayValue = selectedOwnerNames.length === 1
                                            ? selectedOwnerNames[0]
                                            : `${selectedOwnerNames.length} selected`;
                                        titleValue = selectedOwnerNames.join(', ');
                                    }
                                    // Handle other array filters
                                    else if (Array.isArray(filter.value)) {
                                        displayValue = `${filter.value.length} selected`;
                                        titleValue = filter.value.join(', ');
                                    }
                                    // Handle text filters
                                    else {
                                        displayValue = String(filter.value).substring(0, 15);
                                        titleValue = String(filter.value);
                                    }

                                    return (
                                        <span
                                            key={key}
                                            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs bg-green-50 text-gray-700 rounded-full border border-blue-200"
                                        >
                                            <span className="font-medium">
                                                {displayLabel}:
                                            </span>
                                            <span className="text-brandColor" title={titleValue}>
                                                {displayValue}
                                            </span>
                                            <button
                                                onClick={() => {
                                                    const newFilters = { ...activeFilters };
                                                    delete newFilters[key];
                                                    setActiveFilters(newFilters);
                                                    const newTempFilters = { ...tempFilters };
                                                    delete newTempFilters[key];
                                                    setTempFilters(newTempFilters);
                                                }}
                                                className="ml-1 text-gray-400 hover:text-red-600 transition-colors"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Table Container Wrapper with Padding */}
            <div className="px-6 flex-1 flex flex-col min-h-0">
                {/* Table Container */}
                <div
                    ref={tableContainerRef}
                    className="mt-4 bg-white border border-gray-200 rounded-lg shadow-sm overflow-auto flex-1 min-h-0"
                >
                <table className="w-full border-collapse">
                    {/* Header Row */}
                    <thead
                        ref={headerRef}
                        className="sticky top-0 bg-brandColor z-20"
                    >
                        <tr>
                            {/* Fixed Checkbox Column */}
                            <th
                                className="sticky left-0 bg-brandColor p-1.5 text-center border-r border-white/20 z-30"
                                style={{ width: '50px', minWidth: '50px', maxWidth: '50px' }}
                                data-tour="select-all"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectAll}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    className="w-4 h-4 text-white border-white rounded focus:ring-white"
                                />
                            </th>

                            {/* Dynamic Columns */}
                            {displayColumns.map((column, colIndex) => {
                                const canFilter = column.col_name !== 'owner' && column.col_name !== 'last_remark';
                                const isFiltered = hasActiveFilter(column.col_name);
                                const isFirstColumn = colIndex === 0;
                                const isFirstFilterableColumn = canFilter && displayColumns.slice(0, colIndex).every(c => c.col_name === 'owner' || c.col_name === 'last_remark');

                                return (
                                    <th
                                        key={column.col_name}
                                        className="px-2 py-1.5 text-left font-medium text-sm text-white border-r border-white/20"
                                        style={column.col_name === 'phone'
                                            ? { minWidth: '300px', maxWidth: '380px' }
                                            : { minWidth: '175px', maxWidth: '250px' }}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div
                                                className="flex items-center gap-1 cursor-pointer hover:text-white/80 flex-1 min-w-0"
                                                onClick={() => handleSort(column.col_name)}
                                                data-tour={isFirstColumn ? "column-header" : undefined}
                                                title={column.col_label}
                                            >
                                                <span className="truncate">{column.col_label}</span>
                                                {sortColumn === column.col_name && (
                                                    <span className="text-sm flex-shrink-0">{sortDirection === 'ASC' ? '↑' : '↓'}</span>
                                                )}
                                            </div>

                                            {canFilter && (
                                                <button
                                                    onClick={(e) => handleFilterIconClick(column.col_name, e)}
                                                    className={`p-1 rounded hover:bg-white/20 transition-colors ${
                                                        isFiltered ? 'text-yellow-300' : 'text-white/70 hover:text-white'
                                                    }`}
                                                    title="Filter column"
                                                    data-tour={isFirstFilterableColumn ? "column-filter" : undefined}
                                                >
                                                    <Filter className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    {/* Table Body - key forces complete re-render when data changes */}
                    <tbody key={`tbody-${leads.length}-${leads.slice(0, 3).map(l => getRowId(mode, l as any)).join('-')}`}>
                        {leads.map((lead, rowIndex) => {
                            // Per-screen row identity — a screen returning more than one
                            // row per customer needs a key that isn't the lead's uuid.
                            const rowId = getRowId(mode, lead as any);
                            return (
                            <tr
                                key={rowId}
                                className={cn(
                                    "border-b border-gray-100 hover:bg-gray-50 transition-colors",
                                    selectedRows.has(rowId) && "bg-green-50",
                                    // Highlight rows due today with amber/yellow background
                                    (lead as any).is_due_today ? "bg-amber-50 hover:bg-amber-100" :
                                    rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
                                )}
                                data-tour={rowIndex === 0 ? "table-row" : undefined}
                            >
                                {/* Fixed Checkbox Cell */}
                                <td
                                    className="sticky left-0 p-1.5 text-center border-r border-gray-300 bg-white z-10 shadow-md"
                                    style={{ width: '50px', minWidth: '50px', maxWidth: '50px' }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedRows.has(rowId)}
                                        onChange={(e) => handleRowSelect(rowId, e.target.checked)}
                                        className="w-3.5 h-3.5 text-brandColor border-gray-300 rounded focus:ring-brandColor"
                                    />
                                </td>

                                {/* Dynamic Data Cells */}
                                {displayColumns.map((column) => {
                                    const isClickable = ['phone'].includes(column.col_name);
                                    const isPhoneColumn = column.col_name === 'phone';

                                    // Phone column with special rendering
                                    if (isPhoneColumn) {
                                        const hasEvents = lead.events && Array.isArray(lead.events) && lead.events.length > 0;
                                        const phoneNumber = getCellValue(lead, column.col_name);
                                        const hasPhone = phoneNumber && phoneNumber.trim() !== '';
                                        const canCall = outboundConfig && hasPhone && !isCallInProgress;
                                        const hasPendingAbandonedCart = (lead as any).has_pending_abandoned_cart;

                                        return (
                                            <td
                                                key={column.col_name}
                                                className="px-2 py-1.5 border-r border-gray-200 text-sm text-gray-900 cursor-pointer hover:text-brandColor font-medium"
                                                style={{ minWidth: '300px', maxWidth: '380px' }}
                                                title={phoneNumber}
                                                onClick={() => onLeadClick?.(lead)}
                                            >
                                                <div className="flex items-center justify-between gap-2">
                                                    {/* Phone number - left side */}
                                                    <div className="flex items-center gap-2">
                                                        <span className="truncate">
                                                            {formatPhoneNumber(phoneNumber)}
                                                        </span>
                                                        {/* Abandoned cart badge */}
                                                        {hasPendingAbandonedCart && (
                                                            <span
                                                                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded"
                                                                title="Has pending abandoned cart"
                                                            >
                                                                <ShoppingCart className="h-3 w-3" />
                                                                Cart
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Icons/badges - right side */}
                                                    <div className="flex items-center gap-1 flex-shrink-0">
                                                        {/* Copy phone button */}
                                                        {hasPhone && (
                                                            <button
                                                                onClick={(e) => handleCopyPhone(phoneNumber, e)}
                                                                className={`rounded p-1 transition-colors ${copiedPhone === phoneNumber ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                                                                title={copiedPhone === phoneNumber ? 'Copied!' : 'Copy phone number'}
                                                            >
                                                                {copiedPhone === phoneNumber ? (
                                                                    <Check className="h-3.5 w-3.5" />
                                                                ) : (
                                                                    <Copy className="h-3.5 w-3.5" />
                                                                )}
                                                            </button>
                                                        )}

                                                        {/* Phone icon for calling - only if config exists and lead has phone */}
                                                        {canCall && (
                                                            <button
                                                                onClick={(e) => handleInitiateCall(lead, e)}
                                                                className="text-green-600 hover:text-green-700 hover:bg-green-50 rounded p-1 transition-colors"
                                                                title="Click to call"
                                                                disabled={isCallInProgress}
                                                            >
                                                                <Phone className="h-4 w-4" />
                                                            </button>
                                                        )}

                                                        {/* Calendar icon if events exist */}
                                                        {hasEvents && (
                                                            <Calendar
                                                                className="h-4 w-4 text-brandColor"
                                                                aria-label={`${lead.events.length} event(s)`}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        );
                                    }

                                    // RTO Status — colored badge
                                    if (column.col_name === 'rto_status') {
                                        const statusDesc: string = (lead as any).failed_desc || '';
                                        const sl = statusDesc.toLowerCase();
                                        const badgeColor = sl.includes('delivered')
                                            ? 'bg-red-100 text-red-700 border border-red-200'
                                            : sl.includes('transit') || sl.includes('out for')
                                            ? 'bg-orange-100 text-orange-700 border border-orange-200'
                                            : sl.includes('requested') || sl.includes('marked')
                                            ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                                            : 'bg-gray-100 text-gray-600 border border-gray-200';
                                        return (
                                            <td
                                                key={column.col_name}
                                                className="px-2 py-1.5 border-r border-gray-200 text-sm"
                                                style={{ minWidth: '175px', maxWidth: '250px' }}
                                            >
                                                {statusDesc ? (
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeColor}`}>
                                                        {statusDesc}
                                                    </span>
                                                ) : <span className="text-gray-400">—</span>}
                                            </td>
                                        );
                                    }

                                    // Order Source — agent-placed orders get a colored badge so
                                    // they stand out from the storefront majority; Direct stays
                                    // grey. 'Agent (untagged)' is amber: the order IS agent-placed,
                                    // but the agent skipped the tag, so the name is unknown and
                                    // that's worth seeing rather than smoothing over.
                                    if (column.col_name === 'order_source') {
                                        const source: string = (lead as any).order_source || 'direct';
                                        const label: string = (lead as any).order_source_label || 'Direct';
                                        const badgeColor = source === 'agent'
                                            ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                                            : source === 'agent_untagged'
                                            ? 'bg-amber-100 text-amber-700 border border-amber-200'
                                            : 'bg-gray-100 text-gray-600 border border-gray-200';
                                        return (
                                            <td
                                                key={column.col_name}
                                                className="px-2 py-1.5 border-r border-gray-200 text-sm"
                                                style={{ minWidth: '150px', maxWidth: '220px' }}
                                            >
                                                <span
                                                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeColor}`}
                                                    title={(lead as any).order_agent_tag || label}
                                                >
                                                    {label}
                                                </span>
                                            </td>
                                        );
                                    }

                                    // Confirm Orders: Ships By / Why Call as badges, so the rows
                                    // leaving tonight and the riskiest customers stand out.
                                    if (column.col_name === 'ships_by' || column.col_name === 'cod_tier') {
                                        const isShips = column.col_name === 'ships_by';
                                        const label: string = (lead as any)[column.col_name] || '—';
                                        const rank = (lead as any).ships_by_rank as number | undefined;
                                        const badgeColor = isShips
                                            ? (rank === 1 ? 'bg-red-100 text-red-700 border border-red-200'
                                              : rank === 2 ? 'bg-orange-100 text-orange-700 border border-orange-200'
                                              : 'bg-gray-100 text-gray-600 border border-gray-200')
                                            : ((lead as any).cod_tier_key === 'returned_before'
                                              ? 'bg-red-50 text-red-700 border border-red-200'
                                              : 'bg-blue-50 text-blue-700 border border-blue-200');
                                        return (
                                            <td
                                                key={column.col_name}
                                                className="px-2 py-1.5 border-r border-gray-200 text-sm"
                                                style={{ minWidth: '140px', maxWidth: '220px' }}
                                            >
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeColor}`}>
                                                    {label}
                                                </span>
                                            </td>
                                        );
                                    }

                                    // EDD Priority — colored badge (breach state)
                                    if (column.col_name === 'edd_priority') {
                                        const priority: string = (lead as any).priority || 'In Transit';
                                        const badgeColor = priority === 'Breached'
                                            ? 'bg-orange-100 text-orange-700 border border-orange-200'
                                            : priority === 'Due Today'
                                            ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                                            : priority === 'Due Soon'
                                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                            : 'bg-gray-100 text-gray-600 border border-gray-200';
                                        return (
                                            <td
                                                key={column.col_name}
                                                className="px-2 py-1.5 border-r border-gray-200 text-sm"
                                                style={{ minWidth: '140px', maxWidth: '200px' }}
                                            >
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeColor}`}>
                                                    {priority}
                                                </span>
                                            </td>
                                        );
                                    }

                                    // EDD Ageing — colored badge on the same scale the Priority
                                    // column uses, so a breached row reads red in both places.
                                    if (column.col_name === 'edd_ageing') {
                                        const days = (lead as any).days_to_edd as number | null | undefined;
                                        const badgeColor = days === null || days === undefined
                                            ? 'bg-gray-100 text-gray-600 border border-gray-200'
                                            : days < 0
                                            ? 'bg-orange-100 text-orange-700 border border-orange-200'
                                            : days === 0
                                            ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                                            : days <= 2
                                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                            : 'bg-gray-100 text-gray-600 border border-gray-200';
                                        return (
                                            <td
                                                key={column.col_name}
                                                className="px-2 py-1.5 border-r border-gray-200 text-sm"
                                                style={{ minWidth: '110px', maxWidth: '150px' }}
                                            >
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeColor}`}>
                                                    {getCellValue(lead, column.col_name)}
                                                </span>
                                            </td>
                                        );
                                    }

                                    // Dispatch Ageing — colored badge, escalates the longer a
                                    // dispatched shipment has been in the courier's hands
                                    if (column.col_name === 'dispatch_ageing') {
                                        const days = (lead as any).dispatch_ageing_days as number | null | undefined;
                                        const badgeColor = days === null || days === undefined
                                            ? 'bg-gray-100 text-gray-600 border border-gray-200'
                                            : days > 14
                                            ? 'bg-red-100 text-red-700 border border-red-200'
                                            : days > 7
                                            ? 'bg-orange-100 text-orange-700 border border-orange-200'
                                            : days > 3
                                            ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                                            : 'bg-gray-100 text-gray-600 border border-gray-200';
                                        return (
                                            <td
                                                key={column.col_name}
                                                className="px-2 py-1.5 border-r border-gray-200 text-sm"
                                                style={{ minWidth: '120px', maxWidth: '160px' }}
                                            >
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeColor}`}>
                                                    {getCellValue(lead, column.col_name)}
                                                </span>
                                            </td>
                                        );
                                    }

                                    // NDR 1/2/3 — date over reason, with the courier's own remark
                                    // and location on hover. NDR 1 also carries the "+N earlier"
                                    // hint when the shipment had more than three attempts.
                                    if (NDR_COLUMNS.includes(column.col_name as any)) {
                                        const attempt = getNdrAttempt(lead, column.col_name);
                                        const moreCount = (lead as any).ndr_more_count as number | undefined;
                                        const showMore = column.col_name === 'ndr_1' && !!moreCount && moreCount > 0;
                                        const tooltip = attempt
                                            ? [attempt.reason, attempt.courier_remark, attempt.location]
                                                  .filter(Boolean).join('\n')
                                            : undefined;
                                        return (
                                            <td
                                                key={column.col_name}
                                                className="px-2 py-1.5 border-r border-gray-200 text-sm text-gray-900"
                                                style={{ minWidth: '150px', maxWidth: '200px' }}
                                                title={tooltip}
                                            >
                                                {attempt ? (
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-xs text-gray-500">
                                                            {formatNdrDate(attempt.attempt_date) || '—'}
                                                            {showMore && (
                                                                <span className="ml-1 text-gray-400">
                                                                    (+{moreCount} earlier)
                                                                </span>
                                                            )}
                                                        </span>
                                                        <span className="truncate">{attempt.reason || '—'}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                            </td>
                                        );
                                    }

                                    // Product Information — stacked list of cart/order line items
                                    if (column.col_name === 'product_info') {
                                        const items = (lead as any).lineItems as any[] | undefined;
                                        return (
                                            <td
                                                key={column.col_name}
                                                className="px-2 py-1.5 border-r border-gray-200 text-sm text-gray-900"
                                                style={{ minWidth: '220px', maxWidth: '320px' }}
                                                title={getCellValue(lead, column.col_name)}
                                            >
                                                {items && items.length > 0 ? (
                                                    <div className="flex flex-col gap-0.5">
                                                        {items.slice(0, 3).map((it, i) => (
                                                            <div key={i} className="flex items-center gap-1 truncate">
                                                                <span className="truncate">{it.title || it.variant_title || 'Product'}</span>
                                                                {it.title && it.variant_title && (
                                                                    <span className="flex-shrink-0 text-xs text-gray-400">({it.variant_title})</span>
                                                                )}
                                                                {it.quantity > 1 && (
                                                                    <span className="flex-shrink-0 text-xs text-gray-500">×{it.quantity}</span>
                                                                )}
                                                            </div>
                                                        ))}
                                                        {items.length > 3 && (
                                                            <span className="text-xs text-gray-400">+{items.length - 3} more</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                            </td>
                                        );
                                    }

                                    // Regular columns
                                    return (
                                        <td
                                            key={column.col_name}
                                            className={`px-2 py-1.5 border-r border-gray-200 text-sm text-gray-900 ${isClickable ? 'cursor-pointer hover:text-brandColor font-medium' : ''}`}
                                            style={{ minWidth: '175px', maxWidth: '250px' }}
                                            title={getCellValue(lead, column.col_name)}
                                            onClick={isClickable ? () => onLeadClick?.(lead) : undefined}
                                        >
                                            <div className="truncate">
                                                {getCellValue(lead, column.col_name)}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                        })}
                    </tbody>
                </table>

                {/* Empty State */}
                {leads.length === 0 && !isLoading && (
                    <div className="flex flex-col items-center justify-center py-16">
                        <div className="text-gray-400 mb-4">
                            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                        </div>
                        <p className="text-gray-500 font-medium">No leads found</p>
                        <p className="text-gray-400 text-sm mt-1">Try adjusting your filters or add a new lead</p>
                    </div>
                )}

                {/* Loading Overlay */}
                {isLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-40 rounded-lg">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-3 border-brandColor border-t-transparent mx-auto mb-3"></div>
                            <p className="text-gray-600 font-medium">Loading leads...</p>
                        </div>
                    </div>
                )}

                {/* Filter Popover */}
                {filterPopoverColumn && filterPopoverPosition && (
                    <>
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 z-40"
                            onClick={closeFilterPopover}
                        />
                        {/* Popover */}
                        <div
                            className="fixed bg-white border border-gray-200 rounded-lg shadow-xl p-4 z-50"
                            style={{
                                top: `${filterPopoverPosition.top}px`,
                                left: `${filterPopoverPosition.left}px`,
                                minWidth: '280px',
                                maxWidth: '320px'
                            }}
                        >
                            <div className="mb-3 flex items-center justify-between">
                                <h4 className="font-semibold text-sm text-gray-900">
                                    Filter: {displayColumns.find(c => c.col_name === filterPopoverColumn)?.col_label || filterPopoverColumn}
                                </h4>
                                <button
                                    onClick={closeFilterPopover}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>

                            {/* Apply/Clear buttons at TOP for better visibility */}
                            <div className="mb-3 flex gap-2">
                                <button
                                    onClick={() => {
                                        applyFilters();
                                        closeFilterPopover();
                                    }}
                                    className="flex-1 px-3 py-1.5 text-sm bg-brandColor text-white rounded hover:bg-brandColor/90 transition-colors font-medium"
                                >
                                    Apply
                                </button>
                                <button
                                    onClick={() => {
                                        if (filterPopoverColumn) {
                                            // Clear this column's filter
                                            const newFilters = { ...tempFilters };
                                            delete newFilters[filterPopoverColumn];
                                            setTempFilters(newFilters);

                                            const newActiveFilters = { ...activeFilters };
                                            delete newActiveFilters[filterPopoverColumn];
                                            setActiveFilters(newActiveFilters);
                                        }
                                        closeFilterPopover();
                                    }}
                                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors font-medium"
                                >
                                    Clear
                                </button>
                            </div>

                            <div className="space-y-3">
                                {(() => {
                                    const column = displayColumns.find(c => c.col_name === filterPopoverColumn);
                                    if (!column) return null;

                                    return renderFilterInput(column);
                                })()}
                            </div>
                        </div>
                    </>
                )}
            </div>
            </div>

            {/* Pagination and Stats - Fixed Footer */}
            <div className="bg-white border-t border-gray-200 px-6 py-3 mt-4 flex-shrink-0">
                <div className="flex items-center justify-between">
                    {/* Results Stats */}
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}</span> to{' '}
                            <span className="font-semibold text-gray-900">{Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}</span> of{' '}
                            <span className="font-semibold text-gray-900">{pagination.totalItems}</span> results
                        </span>
                        {selectedRows.size > 0 && (
                            <span className="px-2 py-0.5 text-xs bg-green-100 text-brandColor rounded font-medium">
                                {selectedRows.size} selected
                            </span>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex items-center gap-3">
                        {/* Items per page */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Show:</span>
                            <select
                                value={pagination.itemsPerPage}
                                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                className="px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-brandColor focus:border-transparent bg-white"
                            >
                                {[10, 15, 25, 50, 100].map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>

                        {/* Page Navigation */}
                        <div className="flex items-center gap-1">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handlePageChange(1)}
                                disabled={pagination.currentPage === 1}
                                leftIcon={<ChevronsLeft className="h-3.5 w-3.5" />}
                            />
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                                disabled={pagination.currentPage === 1}
                                leftIcon={<ChevronLeft className="h-3.5 w-3.5" />}
                            />

                            <span className="px-3 py-1 text-sm bg-gray-50 rounded border border-gray-200">
                                <span className="text-gray-600">Page</span>
                                <span className="font-semibold text-gray-900 mx-1">{pagination.currentPage}</span>
                                <span className="text-gray-600">of {pagination.totalPages}</span>
                            </span>

                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                                disabled={pagination.currentPage === pagination.totalPages}
                                rightIcon={<ChevronRight className="h-3.5 w-3.5" />}
                            />
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handlePageChange(pagination.totalPages)}
                                disabled={pagination.currentPage === pagination.totalPages}
                                rightIcon={<ChevronsRight className="h-3.5 w-3.5" />}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Change Owner Modal */}
            {isChangeOwnerModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-4">Change Owner</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select New Owner
                                </label>
                                <select
                                    value={selectedNewOwner}
                                    onChange={handleOwnerChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                >
                                    <option value="">Select a user...</option>
                                    {users.map((user) => (
                                        <option key={user.uuid} value={user.uuid}>
                                            {user.userName} ({user.userEmail})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Remark (Required)
                                </label>
                                <textarea
                                    value={ownerChangeRemark}
                                    onChange={handleOwnerRemarkChange}
                                    rows={3}
                                    placeholder="Enter reason for owner change..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent resize-none"
                                />
                            </div>
                            <p className="text-sm text-gray-600">
                                {selectedRows.size} lead(s) will be assigned to the new owner.
                            </p>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <Button
                                variant="outline"
                                onClick={handleCloseOwnerModal}
                                disabled={isProcessingBulkAction}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={processBulkOwnerChange}
                                disabled={!selectedNewOwner || !ownerChangeRemark.trim() || isProcessingBulkAction}
                                className="flex-1"
                            >
                                {isProcessingBulkAction ? 'Processing...' : 'Change Owner'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Reminder Modal */}
            {isBulkReminderModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-4">Add Reminder to {selectedRows.size} Lead(s)</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Reminder Title *
                                </label>
                                <input
                                    type="text"
                                    value={reminderTitle}
                                    onChange={handleReminderTitleChange}
                                    placeholder="Enter reminder title..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Date & Time *
                                </label>
                                <input
                                    type="datetime-local"
                                    value={reminderDateTime}
                                    onChange={handleReminderDateTimeChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Alert Before
                                </label>
                                <select
                                    value={reminderAlertBefore}
                                    onChange={handleReminderAlertChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                >
                                    <option value="15 min">15 min</option>
                                    <option value="30 min">30 min</option>
                                    <option value="1 hour">1 hour</option>
                                    <option value="2 hours">2 hours</option>
                                    <option value="1 day">1 day</option>
                                    <option value="2 days">2 days</option>
                                </select>
                            </div>
                            <p className="text-sm text-gray-600">
                                A reminder will be added to {selectedRows.size} selected lead(s).
                            </p>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <Button
                                variant="outline"
                                onClick={handleCloseReminderModal}
                                disabled={isProcessingBulkAction}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={processBulkReminder}
                                disabled={!reminderTitle.trim() || !reminderDateTime || isProcessingBulkAction}
                                className="flex-1"
                            >
                                {isProcessingBulkAction ? 'Adding...' : 'Add Reminder'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {heldBack && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
                        <h3 className="text-lg font-semibold mb-2 text-amber-700">
                            {heldBack.leads.length + heldBack.ordered.length} lead(s) not assigned
                        </h3>
                        {heldBack.ordered.length > 0 && (
                            <>
                                <p className="text-sm text-gray-700 mb-2">
                                    {heldBack.ordered.length} customer(s) have already placed an order for their cart.
                                    Calling them risks a duplicate order.
                                </p>
                                <div className="max-h-44 overflow-y-auto border border-amber-200 rounded-md divide-y divide-amber-100 mb-4">
                                    {heldBack.ordered.map((l) => (
                                        <div key={l.lead_uuid} className="px-3 py-2 text-sm flex justify-between gap-3">
                                            <span className="truncate">{l.name || l.phone || 'Lead'}</span>
                                            <span className="text-gray-500 shrink-0">
                                                {l.order_number || 'Order'}
                                                {l.ordered_at ? ` · ${new Date(l.ordered_at).toLocaleString()}` : ''}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        {heldBack.leads.length > 0 && (
                            <>
                                <p className="text-sm text-gray-700 mb-2">
                                    {heldBack.leads.length} customer(s) already belong to an agent who has worked them.
                                    Reassigning moves the customer away from them (super admins only).
                                </p>
                                <div className="max-h-44 overflow-y-auto border border-amber-200 rounded-md divide-y divide-amber-100 mb-4">
                                    {heldBack.leads.map((l) => (
                                        <div key={l.lead_uuid} className="px-3 py-2 text-sm flex justify-between gap-3">
                                            <span className="truncate">{l.name || l.phone || 'Lead'}</span>
                                            <span className="text-gray-500 shrink-0">
                                                {l.worked_by || 'An agent'} · {new Date(l.worked_at).toLocaleString()}
                                                {l.outcome ? ` · ${l.outcome}` : ''}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        <div className="flex gap-3">
                            <Button variant="outline" className="flex-1" onClick={() => setHeldBack(null)}>
                                Don't assign these
                            </Button>
                            <Button
                                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                                onClick={async () => {
                                    const hb = heldBack;
                                    setHeldBack(null);
                                    const res = await apiRequest('/leads/bulk-assign', {
                                        method: 'POST',
                                        body: JSON.stringify({
                                            lead_uuids: [...hb.leads, ...hb.ordered].map((l) => l.lead_uuid),
                                            owner_uuid: hb.ownerUuid,
                                            remarks: hb.remark,
                                            include_recently_worked: true,
                                            include_ordered: true,
                                        }),
                                    });
                                    showToast(res.message || (res.success ? 'Assigned' : 'Failed to assign'),
                                              res.success ? 'success' : 'error');
                                    if (res.success) await fetchLeads();
                                }}
                            >
                                Assign anyway
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {isDeleteConfirmModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-4 text-red-600">Confirm Delete</h3>
                        <p className="text-gray-700 mb-4">
                            Are you sure you want to delete {selectedRows.size} selected lead(s)? This action cannot be undone.
                        </p>
                        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                            <p className="text-sm text-red-800">
                                ⚠️ This will permanently remove all selected leads and their associated data.
                            </p>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <Button
                                variant="outline"
                                onClick={handleCloseDeleteModal}
                                disabled={isProcessingBulkAction}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={processBulkDelete}
                                disabled={isProcessingBulkAction}
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                            >
                                {isProcessingBulkAction ? 'Deleting...' : 'Delete All'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Lead Modal - Only for Agent Orders page */}
            {agentCreatedOnly && (
                <AddLeadModal
                    isOpen={isAddLeadModalOpen}
                    onClose={() => setIsAddLeadModalOpen(false)}
                    refreshData={fetchLeads}
                />
            )}

            {/* Advanced Filters Modal */}
            <PipelineFiltersEnhanced
                isOpen={isAdvancedFiltersOpen}
                onClose={() => setIsAdvancedFiltersOpen(false)}
                onApplyFilters={handleAdvancedFiltersApply}
                columns={columns.map(col => ({
                    col_id: col.uuid || col.col_name,
                    col_name: col.col_name,
                    col_label: col.col_label,
                    col_type: col.col_type,
                    col_required: false,
                    col_order: col.col_order,
                    options: col.options
                }))}
                currentFilters={convertLeadsFiltersToPipelineFormat(activeFilters)}
                isLoading={isLoading}
            />

            {/* Export Modal */}
            <ExportModal
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                selectedRows={Array.from(selectedRows)}
                totalFilteredCount={pagination.totalItems}
                currentFilters={convertActiveFiltersToBackend(activeFilters)}
                agentCreatedOnly={agentCreatedOnly}
                leadBucketFilter={leadBucketFilter}
                apiEndpoint={apiEndpoint}
            />

            {/* Initiate Call Modal */}
            {isCallModalOpen && selectedCallLead && (
                <InitiateCallModal
                    isOpen={isCallModalOpen}
                    onClose={handleCloseCallModal}
                    leadUuid={selectedCallLead.uuid}
                    leadName={selectedCallLead.name}
                    leadPhone={selectedCallLead.phone}
                    orderUuid={selectedCallLead.orderUuid ?? null}
                />
            )}
        </div>
    );
});

EnhancedLeadsTable.displayName = 'EnhancedLeadsTable';

export default EnhancedLeadsTable;